<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading.initial || loading.deleting"/>
        <div class="flex flex-col flex-1 min-h-0">
            <div class="flex flex-col flex-1 min-h-0">
                <div class="stage-page-header">
                    <div class="stage-page-header-row">
                        <div class="stage-page-header-main">
                            <div class="flex items-center gap-3">
                                <h1 class="stage-page-title">Position</h1>
                                <identifier-badge :value="documentation.id || '-'" title="Positions-ID"></identifier-badge>
                            </div>
                        </div>
                        <div class="stage-page-header-actions">
                            <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" :to="positionsListPath">
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                            </router-link>
                            <div v-if="isAdmin" v-on-clickaway="closeProjectDropdown" class="relative inline-flex items-center">
                                <button class="btn-stage-icon-plain" type="button" title="Projektzuweisung" aria-label="Projektzuweisung" @click="toggleProjectDropdown">
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.layer-group"></svg-vue>
                                </button>
                                <div
                                    v-show="projectDropdownOpen"
                                    class="origin-top-right absolute top-full right-0 mt-2 w-72 rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                    @click.stop
                                >
                                    <div class="stage-icon-menu-header">Projektzuweisung</div>
                                    <div v-if="!hasAssignedCompany" class="px-3 py-3 text-sm text-gray-500">
                                        Kein Unternehmen zugewiesen.
                                    </div>
                                    <div v-else-if="availableProjects.length < 1" class="px-3 py-3 text-sm text-gray-500">
                                        {{ $t('No projects available.') }}
                                    </div>
                                    <div v-else class="max-h-64 overflow-y-auto">
                                        <label
                                            v-for="project in availableProjects"
                                            :key="'position-project-assignment-' + project.id"
                                            class="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                            @click.stop
                                        >
                                            <input
                                                v-model="selectedProjectIds"
                                                :value="Number(project.id)"
                                                class="form-checkbox h-4 w-4 text-gray-700"
                                                type="checkbox"
                                                @click.stop
                                                @change="saveProjectAssignments"
                                            >
                                            <img
                                                :alt="project.title || 'Projektbild'"
                                                :src="project.image_url ? project.image_url : '/images/default/icon.png'"
                                                class="h-6 w-6 rounded-sm object-cover ml-2 mr-2"
                                            >
                                            <span class="text-sm text-gray-800 truncate">{{ project.title }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div v-on-clickaway="closeHeaderActionsMenu" class="relative inline-flex items-center">
                                <button class="btn-stage-icon-plain" type="button" title="Optionen" aria-label="Optionen" @click="toggleHeaderActionsMenu">
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                </button>
                                <div
                                    v-show="headerActionsOpen"
                                    class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                >
                                    <div class="py-1 flex flex-col">
                                        <router-link
                                            v-if="documentation.can_edit"
                                            :to="'/workspace/positions/' + $route.params.id + '/edit'"
                                            class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            @click="headerActionsOpen = false"
                                        >
                                            Position umbenennen
                                        </router-link>
                                        <router-link
                                            :to="'/workspace/positions/' + $route.params.id + '/settings'"
                                            class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            @click="headerActionsOpen = false"
                                        >
                                            Prozessschritte bearbeiten
                                        </router-link>
                                        <button
                                            v-if="documentation.can_manage_permissions && documentation.can_delete"
                                            type="button"
                                            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            @click="headerActionsOpen = false; deletePositionModal = true"
                                        >
                                            Position löschen
                                        </button>
                                    </div>
                                </div>
                            </div>
                    </div>
                </div>
                </div>

                <div class="stage-page-gutters stage-page-fill pt-3 h-0">
                    <div class="w-full h-full min-h-0 flex">
                    <div class="w-full h-full bg-white shadow sm:rounded overflow-hidden flex flex-col min-h-0">
                        <div class="stage-header-surface border-b border-ultralight">
                            <div class="flex items-stretch justify-between min-h-[56px]">
                                <div class="min-w-0 flex items-stretch">
                                    <div v-if="showHeaderProjectCell && assignedProjectsForHeader.length > 0" class="px-4 flex items-center border-r border-ultralight">
                                        <div class="flex items-center flex-shrink-0">
                                            <template v-for="(project, projectIndex) in assignedProjectsForHeader" :key="'position-header-project-' + Number(project.id) + '-' + projectIndex">
                                                <img
                                                   
                                                    :src="getProjectImageUrl(project)"
                                                    :alt="project.title || 'Projektbild'"
                                                    :title="getProjectTooltip(project)"
                                                    :class="projectIndex === assignedProjectsForHeader.length - 1 ? '' : '-mr-5'"
                                                    class="h-7 w-7 rounded-sm object-cover border border-white"
                                                >
                                            </template>
                                        </div>
                                    </div>
                                    <div class="min-w-0 flex items-center px-4 py-4">
                                        <img
                                            v-if="assignedCompany"
                                            :alt="assignedCompany.name || 'Unternehmen'"
                                            :src="getUserAvatarUrl(assignedCompany)"
                                            @error="handleAvatarImageError($event, assignedCompany)"
                                            class="h-7 w-7 rounded-sm object-cover border border-white mr-2 flex-shrink-0"
                                        >
                                        <div class="text-l font-semibold text-gray-900 truncate">{{ documentation.title || 'Position' }}</div>
                                    </div>
                                </div>
                                <div class="w-32 border-l border-ultralight">
                                    <button
                                        :disabled="!documentation.can_edit || loading.saving"
                                        class="btn-stage-primary w-full h-full border-0"
                                        style="border-radius: 0;"
                                        type="button"
                                        @click="saveDocumentation"
                                    >
                                        Speichern
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="flex-1 min-h-0 flex overflow-hidden">
                        <div class="flex-1 min-h-0 flex flex-col">
                        <div class="px-5 border-b border-bordergray-100">
                            <nav class="-mb-px flex">
                                <button
                                    type="button"
                                    class="mr-8 py-4 px-1 border-b-2 text-base font-medium cursor-pointer"
                                    :class="activeTab === 'steps' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-900'"
                                    @click="activeTab = 'steps'"
                                >
                                    Agenda
                                </button>
                                <button
                                    type="button"
                                    class="py-4 px-1 border-b-2 text-base font-medium cursor-pointer"
                                    :class="activeTab === 'project' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-900'"
                                    @click="activeTab = 'project'"
                                >
                                    Projekt
                                </button>
                                <button
                                    type="button"
                                    class="ml-8 py-4 px-1 border-b-2 text-base font-medium cursor-pointer"
                                    :class="activeTab === 'costs' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-900'"
                                    @click="activeTab = 'costs'"
                                >
                                    Kostenfreigaben
                                </button>
                                <button
                                    type="button"
                                    class="ml-8 py-4 px-1 border-b-2 text-base font-medium cursor-pointer"
                                    :class="activeTab === 'actions' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-900'"
                                    @click="activeTab = 'actions'"
                                >
                                    Aktionsfreigaben
                                </button>
                            </nav>
                        </div>
                        <div :class="['flex-1 min-h-0 overflow-y-auto', (activeTab === 'costs' || activeTab === 'actions') ? 'p-0' : 'p-8']">
                        <div v-if="activeTab === 'project'">
                            <div :class="isAdmin ? 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'">
                                <div v-if="isAdmin">
                                    <label class="stage-filter-label block" for="customer_ticket_reference_manage">Kunden-Ticket-Referenz</label>
                                    <input
                                        id="customer_ticket_reference_manage"
                                        v-model.number="documentation.customer_ticket_reference"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-input block w-full"
                                        min="1"
                                        step="1"
                                        type="number"
                                    >
                                </div>
                                <div>
                                    <label class="stage-filter-label block" for="employee_ticket_reference_manage">Ticket-Referenz</label>
                                    <input
                                        id="employee_ticket_reference_manage"
                                        v-model.number="documentation.employee_ticket_reference"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-input block w-full"
                                        min="1"
                                        step="1"
                                        type="number"
                                    >
                                </div>
                                <div>
                                    <label class="stage-filter-label block" for="gitlab_reference_manage">GitLab-Referenz</label>
                                    <input
                                        id="gitlab_reference_manage"
                                        v-model.number="documentation.gitlab_reference"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-input block w-full"
                                        min="1"
                                        step="1"
                                        type="number"
                                    >
                                </div>
                            </div>
                            <div class="mb-1">
                                <label class="stage-filter-label block">Interne Anmerkung</label>
                            </div>
                            <div class="documentation-editor">
                                <input-wysiwyg
                                    id="position_project_information_body"
                                    v-model="documentation.body"
                                    :readonly="!documentation.can_edit"
                                    :plugins="{images: true, cannedReply: false, attachment: true, shortCode: false, menuPoint: false}"
                                    @selectUploadFile="selectUploadFile"
                                >
                                    <template v-slot:top>
                                        <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                            <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                        </div>
                                    </template>
                                </input-wysiwyg>
                            </div>
                            <div
                                v-if="hasProjectBodyMeta"
                                class="mt-2 flex items-center text-xs text-gray-500"
                            >
                                <img
                                    :alt="documentation.body_updated_by.name || 'User'"
                                    :src="getUserAvatarUrl(documentation.body_updated_by)"
                                    @error="handleAvatarImageError($event, documentation.body_updated_by)"
                                    class="h-5 w-5 rounded-full object-cover mr-2"
                                >
                                <span>{{ formatBodyUpdateMeta(documentation.body_updated_at, documentation.body_updated_by) }}</span>
                            </div>
                            <div v-if="isAdmin" class="mt-4 grid grid-cols-1 gap-4">
                                <div>
                                    <label class="stage-filter-label block mb-1" for="customer_position_description_manage">
                                        Allgemeine Positionsanmerkungen (Kundenansicht)
                                    </label>
                                    <textarea
                                        id="customer_position_description_manage"
                                        v-model="documentation.customer_position_description"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        rows="4"
                                    ></textarea>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-1" for="customer_cost_description_manage">
                                        Allgemeine Anmerkungen zu Kostenfreigaben (Kundenansicht)
                                    </label>
                                    <textarea
                                        id="customer_cost_description_manage"
                                        v-model="documentation.customer_cost_description"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        rows="4"
                                    ></textarea>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-1" for="customer_action_description_manage">
                                        Allgemeine Anmerkungen zu Aktionsfreigaben (Kundenansicht)
                                    </label>
                                    <textarea
                                        id="customer_action_description_manage"
                                        v-model="documentation.customer_action_description"
                                        :readonly="!documentation.can_edit"
                                        class="stage-filter-input form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        rows="4"
                                    ></textarea>
                                </div>
                            </div>
                            <div v-if="documentation.attachments.length > 0" class="mt-4 pb-4">
                                <div class="flex flex-wrap items-start gap-2">
                                    <template v-for="(attachment, index) in documentation.attachments">
                                        <attachment :details="attachment" :remove-button="documentation.can_edit" v-on:remove="removeAttachment(index)"/>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <div v-if="activeTab === 'costs'">
                            <div class="w-full">
                                <div class="sticky top-0 z-40 bg-white border-b border-bordergray-100 p-8">
                                    <div class="flex items-center justify-between gap-4">
                                        <div class="flex-1 min-w-0">
                                            <approval-add-bar
                                                label="Leistungsbaustein"
                                                mode="select"
                                                :value="newEffortServiceCategoryId"
                                                :options="documentation.effort_catalog || []"
                                                :disabled="!documentation.can_edit || (documentation.effort_catalog || []).length < 1"
                                                :can-add="canAddEffort"
                                                @input="newEffortServiceCategoryId = Number($event)"
                                                @add="addEffort"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div v-if="visibleEffortsWithIndex.length > 0" class="px-8 pt-6 pb-8">
                                    <div class="space-y-5">
                                        <div
                                            v-for="({effort, index}) in visibleEffortsWithIndex"
                                            :key="'manage-position-effort-' + (effort.id || ('new-' + index))"
                                            class="rounded border border-bordergray-100 overflow-visible bg-white"
                                        >
                                            <div class="flex">
                                                <div :class="effortStatusStripClass(effort)" class="w-1 flex-shrink-0"></div>

                                                <div class="flex-1 min-w-0">
                                                    <div class="pt-5">
                                                        <div class="px-6 pb-4 border-b border-bordergray-100">
                                                            <div class="text-xl leading-snug font-semibold text-gray-900 break-words">
                                                                {{ getEffortCategoryName(effort.service_category_id) }}
                                                            </div>
                                                            <div v-if="getEffortCategoryDescription(effort.service_category_id)" class="mt-1 text-sm leading-5 text-gray-500 break-words max-w-4xl">
                                                                {{ getEffortCategoryDescription(effort.service_category_id) }}
                                                            </div>
                                                        </div>

                                                        <div class="grid grid-cols-1 lg:grid-cols-12 gap-0">
                                                            <div class="lg:col-span-8 2xl:col-span-9 px-6 py-5 lg:pr-6">
                                                                <div>
                                                                    <label class="stage-filter-label block mb-1">
                                                                        Kurzbeschreibung (Kundenansicht)
                                                                    </label>
                                                                    <textarea
                                                                        v-model="documentation.efforts[index].short_description"
                                                                        :readonly="isEffortLockedForCurrentUser(effort)"
                                                                        class="stage-filter-input form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                        :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive cursor-not-allowed' : ''"
                                                                        rows="3"
                                                                    ></textarea>
                                                                    <div
                                                                        v-if="hasShortDescriptionUpdateMeta(effort)"
                                                                        class="mt-2 flex items-center text-xs text-gray-500"
                                                                    >
                                                                        <img
                                                                            :alt="(effort.short_description_updated_by && effort.short_description_updated_by.name) || 'User'"
                                                                            :src="getUserAvatarUrl(effort.short_description_updated_by)"
                                                                            @error="handleAvatarImageError($event, effort.short_description_updated_by)"
                                                                            class="h-5 w-5 rounded-full object-cover mr-2"
                                                                        >
                                                                        <span>{{ formatBodyUpdateMeta(effort.short_description_updated_at, effort.short_description_updated_by) }}</span>
                                                                    </div>
                                                                </div>

                                                                <div class="mt-5">
                                                                    <label class="stage-filter-label block mb-1">
                                                                        Zuweisung
                                                                    </label>
                                                                    <div class="border border-bordergray-100 rounded bg-white overflow-hidden" :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive' : ''">
                                                                        <template v-if="(documentation.employee_options || []).length > 0">
                                                                            <template v-if="isAdmin && !isEffortLockedForCurrentUser(effort)">
                                                                                <label
                                                                                    v-for="employee in getAdminAssignableEffortEmployees()"
                                                                                    :key="'effort-' + (effort.id || ('new-' + index)) + '-employee-' + employee.id"
                                                                                    class="flex items-center px-3 py-2 border-b border-bordergray-100 last:border-b-0 cursor-pointer"
                                                                                >
                                                                                    <input
                                                                                        :checked="Number((effort.employee_ids || [])[0] || 0) === Number(employee.id)"
                                                                                        :disabled="isEmployeeAssignmentReadonly(effort)"
                                                                                        class="form-radio h-4 w-4 text-gray-700"
                                                                                        type="radio"
                                                                                        :name="'effort-employee-' + (effort.id || ('new-' + index))"
                                                                                        @change="setEffortEmployee(index, Number(employee.id))"
                                                                                    >
                                                                                    <div class="ml-3 h-5 w-5 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                                        <img
                                                                                            :alt="employee.name || 'Mitarbeiter'"
                                                                                            :src="getEmployeeAvatarUrl(employee)"
                                                                                            @error="handleAvatarImageError($event, employee)"
                                                                                            class="h-full w-full object-cover"
                                                                                        >
                                                                                    </div>
                                                                                    <div class="ml-3 min-w-0">
                                                                                        <div class="text-sm text-gray-800 truncate">{{ employee.name }}</div>
                                                                                        <div class="text-xs text-gray-500 truncate">{{ getEmployeeAssignmentSubtitle(employee) }}</div>
                                                                                    </div>
                                                                                </label>
                                                                            </template>
                                                                            <template v-else-if="isEmployee">
                                                                                <div
                                                                                    v-for="employee in getEffortAssignedEmployees(effort)"
                                                                                    :key="'effort-employee-readonly-' + (effort.id || ('new-' + index)) + '-employee-' + employee.id"
                                                                                    class="flex items-center px-3 py-2 border-b border-bordergray-100 last:border-b-0"
                                                                                >
                                                                                    <div class="h-5 w-5 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                                        <img
                                                                                            :alt="employee.name || 'Mitarbeiter'"
                                                                                            :src="getEmployeeAvatarUrl(employee)"
                                                                                            @error="handleAvatarImageError($event, employee)"
                                                                                            class="h-full w-full object-cover"
                                                                                        >
                                                                                    </div>
                                                                                    <div class="ml-3 min-w-0">
                                                                                        <div class="text-sm text-gray-800 truncate">{{ employee.name }}</div>
                                                                                        <div class="text-xs text-gray-500 truncate">{{ getEmployeeAssignmentSubtitle(employee) }}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </template>
                                                                            <template v-else>
                                                                                <div
                                                                                    v-for="employee in getEffortAssignedEmployees(effort)"
                                                                                    :key="'effort-approved-' + (effort.id || ('new-' + index)) + '-employee-' + employee.id"
                                                                                    class="flex items-center px-3 py-2 border-b border-bordergray-100 last:border-b-0"
                                                                                >
                                                                                    <div class="h-5 w-5 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                                        <img
                                                                                            :alt="employee.name || 'Mitarbeiter'"
                                                                                            :src="getEmployeeAvatarUrl(employee)"
                                                                                            @error="handleAvatarImageError($event, employee)"
                                                                                            class="h-full w-full object-cover"
                                                                                        >
                                                                                    </div>
                                                                                    <div class="ml-3 min-w-0">
                                                                                        <div class="text-sm text-gray-800 truncate">{{ employee.name }}</div>
                                                                                        <div class="text-xs text-gray-500 truncate">{{ getEmployeeAssignmentSubtitle(employee) }}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </template>
                                                                        </template>
                                                                        <div v-if="isEffortLockedForCurrentUser(effort) && (!effort.employee_ids || effort.employee_ids.length < 1)" class="px-3 py-3 text-sm text-gray-800">
                                                                            Keine Zuweisung vorhanden
                                                                        </div>
                                                                        <div v-else-if="(documentation.employee_options || []).length < 1" class="px-3 py-3 text-sm text-gray-500">
                                                                            Keine Zuweisung verfügbar
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div v-if="shouldShowProviderServiceAccount(effort)" class="mt-5">
                                                                    <label class="stage-filter-label block mb-1">
                                                                        Dienstleister-Leistungskonto
                                                                    </label>
                                                                    <input-select
                                                                        v-if="canEditProviderServiceAccount(effort) && getEffortProviderServiceAccountOptions(effort).length > 0"
                                                                        :value="effort.provider_service_account_id"
                                                                        :options="getEffortProviderServiceAccountOptions(effort)"
                                                                        :searchable="getEffortProviderServiceAccountOptions(effort).length > 6"
                                                                        class="stage-filter-select"
                                                                        option-label="name"
                                                                        required
                                                                        @input="setEffortProviderServiceAccount(index, $event)"
                                                                    >
                                                                        <template v-slot:selectedOption="props">
                                                                            <template v-if="!props || !props.anySelected">
                                                                                <span class="block truncate">Leistungskonto auswählen</span>
                                                                            </template>
                                                                            <template v-else>
                                                                                <div class="flex items-center space-x-3 min-w-0">
                                                                                    <img
                                                                                        :src="resolveProviderServiceAccountAvatar(props.option, effort)"
                                                                                        :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                                                        class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                                                    >
                                                                                    <div class="min-w-0">
                                                                                        <span class="block truncate">{{ props.option ? props.option.name : 'Leistungskonto auswählen' }}</span>
                                                                                        <span v-if="props.option" class="block truncate text-xs text-gray-500">{{ providerServiceAccountSubtitle(props.option) }}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </template>
                                                                        </template>
                                                                        <template v-slot:selectOption="props">
                                                                            <div class="flex items-center space-x-3 min-w-0">
                                                                                <img
                                                                                    :src="resolveProviderServiceAccountAvatar(props.option, effort)"
                                                                                    :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                                                    class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                                                >
                                                                                <div class="min-w-0 font-normal">
                                                                                    <span class="block truncate">{{ props.option ? props.option.name : '' }}</span>
                                                                                    <span v-if="props.option" class="block truncate text-xs text-gray-500">{{ providerServiceAccountSubtitle(props.option) }}</span>
                                                                                </div>
                                                                            </div>
                                                                        </template>
                                                                        <template v-slot:notFound>
                                                                            <div class="font-normal block truncate">Keine Leistungskonten verfügbar</div>
                                                                        </template>
                                                                    </input-select>
                                                                    <div
                                                                        v-else-if="getSelectedProviderServiceAccount(effort)"
                                                                        class="flex items-center min-h-[42px] px-3 py-2 rounded border border-bordergray-100 bg-white"
                                                                    >
                                                                        <img
                                                                            :src="resolveProviderServiceAccountAvatar(getSelectedProviderServiceAccount(effort), effort)"
                                                                            :alt="getSelectedProviderServiceAccount(effort).name || 'Leistungskonto'"
                                                                            class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                                        >
                                                                        <div class="ml-3 min-w-0">
                                                                            <div class="text-sm text-gray-800 truncate">{{ getSelectedProviderServiceAccount(effort).name }}</div>
                                                                            <div class="text-xs text-gray-500 truncate">{{ providerServiceAccountSubtitle(getSelectedProviderServiceAccount(effort)) }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <div v-else class="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                                                        Bitte wählen Sie ein aktives Dienstleister-Leistungskonto aus.
                                                                    </div>
                                                                </div>

                                                                <div v-if="effort.approved_by && effort.approved_by.name" class="mt-5">
                                                                    <label class="stage-filter-label block mb-1">
                                                                        Freigabe von
                                                                    </label>
                                                                    <div class="stage-form-control-inactive border rounded px-3 py-2">
                                                                        <div class="flex items-center py-2">
                                                                            <div class="h-8 w-8 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                                <img
                                                                                    :alt="effort.approved_by.name || 'Freigabe'"
                                                                                    :src="getApprovalAvatarUrl(effort.approved_by)"
                                                                                    @error="handleApprovalAvatarImageError($event, effort.approved_by)"
                                                                                    class="h-full w-full object-cover"
                                                                                >
                                                                            </div>
                                                                            <div class="ml-4 min-w-0">
                                                                                <div class="text-sm text-gray-800">{{ effort.approved_by.name }}</div>
                                                                                <div v-if="effort.approved_at" class="text-xs text-gray-500">
                                                                                    {{ getApprovalApprovedAtDateTimeText(effort) }}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    v-if="!isEmployee || isEffortAssignedToCurrentUser(effort)"
                                                                    class="mt-5"
                                                                >
                                                                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                                        <div v-if="shouldShowInternalBillingForEffort(effort)" :class="isEmployee ? 'lg:col-span-2' : ''">
                                                                            <div class="text-sm font-semibold text-gray-800 mb-3">{{ isEmployee ? 'Honorar' : 'Abrechnung (intern)' }}</div>
                                                                            <div :class="isEmployee ? 'w-full grid grid-cols-2 gap-4' : ''">
                                                                                <div>
                                                                                    <label class="stage-filter-label block mb-1">
                                                                                        Aufwand (Std.)
                                                                                    </label>
                                                                                    <input
                                                                                        :value="canCurrentUserSeeEffortAmounts(effort) ? effort.internal_hours : ''"
                                                                                        :readonly="isInternalHoursReadonly(effort)"
                                                                                        class="stage-filter-input form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                        :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive cursor-not-allowed' : ''"
                                                                                        min="0"
                                                                                        step="0.25"
                                                                                        type="number"
                                                                                        @input="setEffortField(index, 'internal_hours', $event.target.value)"
                                                                                    >
                                                                                </div>
                                                                                <div :class="isEmployee ? '' : 'mt-4'">
                                                                                    <label class="stage-filter-label block mb-1">
                                                                                        Stundensatz (EUR / Std.)
                                                                                    </label>
                                                                                    <input
                                                                                        :value="canCurrentUserSeeEffortAmounts(effort) ? (getInternalHourlyRateForEffort(effort) || '') : ''"
                                                                                        readonly
                                                                                        class="stage-filter-input form-input stage-form-control-inactive block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                        :class="isEffortLockedForCurrentUser(effort) ? 'cursor-not-allowed' : ''"
                                                                                        type="text"
                                                                                    >
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div v-if="isAdmin" :class="shouldShowInternalBillingForEffort(effort) ? '' : 'lg:col-span-2'">
                                                                            <div class="text-sm font-semibold text-gray-800 mb-3">Abrechnung</div>
                                                                            <div>
                                                                                <label class="stage-filter-label block mb-1">
                                                                                    Aufwand (Std.)
                                                                                </label>
                                                                                <input
                                                                                    :value="effort.customer_hours"
                                                                                    :readonly="isCustomerHoursReadonly(effort)"
                                                                                    class="stage-filter-input form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                    :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive cursor-not-allowed' : ''"
                                                                                    min="0"
                                                                                    step="0.25"
                                                                                    type="number"
                                                                                    @input="setEffortField(index, 'customer_hours', $event.target.value)"
                                                                                >
                                                                            </div>
                                                                            <div class="mt-4">
                                                                                <label class="stage-filter-label block mb-1">
                                                                                    Stundensatz (EUR / Std.)
                                                                                </label>
                                                                                <input
                                                                                    :value="getReadonlyCustomerHourlyRateForEffort(effort)"
                                                                                    readonly
                                                                                    class="stage-filter-input form-input stage-form-control-inactive block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                    :class="isEffortLockedForCurrentUser(effort) ? 'cursor-not-allowed' : ''"
                                                                                    type="text"
                                                                                >
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div v-if="shouldShowEffortQuantificationFields(effort)" class="mt-6 pt-5 border-t border-bordergray-100">
                                                                        <div class="text-sm font-semibold text-gray-800 mb-3">Quantifizierung</div>
                                                                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                                            <div>
                                                                                <label class="stage-filter-label block mb-1">
                                                                                    Quantifizierungsfaktor (QF)
                                                                                </label>
                                                                                <input
                                                                                    :value="effort.quantification_factor"
                                                                                    :readonly="isEffortQuantificationReadonly(effort)"
                                                                                    class="stage-filter-input form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                    :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive cursor-not-allowed' : ''"
                                                                                    min="0"
                                                                                    step="1"
                                                                                    type="number"
                                                                                    @input="setEffortField(index, 'quantification_factor', $event.target.value)"
                                                                                >
                                                                            </div>
                                                                            <div>
                                                                                <label class="stage-filter-label block mb-1">
                                                                                    Quantifizierungswert (QW)
                                                                                </label>
                                                                                <input
                                                                                    :value="effort.quantification_value"
                                                                                    :readonly="isEffortQuantificationReadonly(effort)"
                                                                                    class="stage-filter-input form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                                    :class="isEffortLockedForCurrentUser(effort) ? 'stage-form-control-inactive cursor-not-allowed' : ''"
                                                                                    min="0"
                                                                                    step="0.01"
                                                                                    type="number"
                                                                                    @input="setEffortField(index, 'quantification_value', $event.target.value)"
                                                                                >
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <aside class="lg:col-span-4 2xl:col-span-3 border-t border-bordergray-100 py-5 lg:border-t-0 lg:border-l">
                                                                <div>
                                                                    <div class="px-6 space-y-1">
                                                                        <div class="text-sm text-gray-600">Netto: {{ formatCurrency(getEffortPreviewNetAmount(effort)) }}</div>
                                                                        <div v-if="shouldShowEffortPreviewQuantification(effort)" class="text-sm text-gray-600">
                                                                            Inkl. QF ({{ getEffortPreviewQuantificationFactor(effort) }} x {{ getEffortPreviewQuantificationValue(effort).toFixed(2) }}, {{ formatCurrency(getEffortPreviewQuantificationAmount(effort)) }})
                                                                        </div>
                                                                        <div class="text-sm text-gray-600">
                                                                            MwSt ({{ getEffortPreviewVatRate(effort).toFixed(2) }}%): {{ formatCurrency(getEffortPreviewVatAmount(effort)) }}
                                                                        </div>
                                                                        <div class="my-2 border-t-2 border-gray-700"></div>
                                                                        <div class="text-sm font-semibold text-gray-700">Summe: {{ formatCurrency(getEffortPreviewGrossAmount(effort)) }}</div>
                                                                    </div>
                                                                </div>
                                                            </aside>
                                                        </div>
                                                    </div>

                                                    <div class="stage-footer-surface border-t border-bordergray-100 px-4 py-0 flex items-stretch justify-between">
                                                        <div class="flex items-center py-4 pr-4">
                                                            <span class="inline-flex items-center text-sm font-medium text-gray-700">
                                                                <span
                                                                    class="inline-block h-2 w-2 rounded-full mr-2"
                                                                    :class="isEffortCancelled(effort) ? 'bg-red-500' : (isEffortApproved(effort) ? 'bg-green-500' : 'bg-gray-400')"
                                                                ></span>
                                                                {{ effortStatusLabel(effort) }}
                                                            </span>
                                                        </div>
                                                        <div class="flex items-stretch">
                                                            <div v-if="isEffortApproved(effort)" class="flex items-center px-4 text-sm text-gray-500">
                                                                <span class="inline-flex items-center">
                                                                    <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.lock-alt-regular"></svg-vue>
                                                                    Freigegebene Kosten sind für die Bearbeitung gesperrt
                                                                </span>
                                                            </div>
                                                            <div v-if="isEmployee && isEffortCheckedIn(effort)" class="flex items-center px-4 text-sm text-gray-500">
                                                                <span class="inline-flex items-center">
                                                                    <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.lock-alt-regular"></svg-vue>
                                                                    Eingecheckte Leistungen sind für die Bearbeitung gesperrt.
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="!isEffortApproved(effort) && !isEffortCancelled(effort)"
                                                                :class="isEmployee ? 'w-auto border-l border-bordergray-100 flex items-center pl-4' : 'min-w-[170px] border-l border-bordergray-100 flex items-stretch'"
                                                            >
                                                                <select
                                                                    v-if="canManageEffortCheckState(effort)"
                                                                    :value="isEffortCheckedIn(effort) ? 'checked_in' : 'checked_out'"
                                                                    :disabled="isEffortCheckStateSelectionDisabled(effort)"
                                                                    :style="checkStateSelectionStyle(isEffortCheckStateSelectionDisabled(effort))"
                                                                    :class="[
                                                                        'form-select w-full h-full px-4 text-sm border-0 bg-transparent focus:ring-0 focus:border-transparent disabled:opacity-100',
                                                                        isEffortCheckStateSelectionDisabled(effort)
                                                                            ? 'text-gray-400 cursor-default'
                                                                            : 'text-gray-700'
                                                                    ]"
                                                                    @change="setEffortCheckState(index, $event.target.value === 'checked_in')"
                                                                >
                                                                    <option value="checked_in">Eingecheckt</option>
                                                                    <option value="checked_out">Ausgecheckt</option>
                                                                </select>
                                                                <span v-else class="inline-flex items-center py-4 pr-4 text-sm text-gray-600 whitespace-nowrap">
                                                                    {{ isEffortCheckedIn(effort) ? 'Eingecheckt' : 'Ausgecheckt' }}
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="isEmployee && canRemoveEffort(effort)"
                                                                :class="['flex items-center pl-4', isEffortApproved(effort) ? '' : 'border-l border-bordergray-100']"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    class="inline-flex items-center justify-center p-1 text-gray-700 cursor-pointer"
                                                                    @click="confirmRemoveEffort(index, effort)"
                                                                >
                                                                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                                </button>
                                                            </div>
                                                            <div
                                                                v-else-if="canShowEffortActionsMenu(effort)"
                                                                v-on-clickaway="closeEffortActionsMenu"
                                                                :class="['relative flex items-center pl-4', isEffortApproved(effort) ? '' : 'border-l border-bordergray-100']"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    class="inline-flex items-center justify-center p-1 text-gray-700 cursor-pointer"
                                                                    @click.stop="toggleEffortActionsMenu(index, effort)"
                                                                >
                                                                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                                        <circle cx="12" cy="5" r="1.8"></circle>
                                                                        <circle cx="12" cy="12" r="1.8"></circle>
                                                                        <circle cx="12" cy="19" r="1.8"></circle>
                                                                    </svg>
                                                                </button>
                                                                <div
                                                                    v-show="openEffortActionsMenuKey === getEffortActionsMenuKey(index, effort)"
                                                                    class="origin-bottom-right absolute right-0 bottom-full mb-1 w-48 rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                                                    @click.stop
                                                                >
                                                                    <button
                                                                        v-if="canChangeEffortAssignment(effort)"
                                                                        type="button"
                                                                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        @click="openAssignmentChangeModal(index, effort)"
                                                                    >
                                                                        {{ $t('Change assignment') }}
                                                                    </button>
                                                                    <button
                                                                        v-if="isEffortApproved(effort)"
                                                                        type="button"
                                                                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        @click="confirmCancelEffort(index, effort)"
                                                                    >
                                                                        Stornieren
                                                                    </button>
                                                                    <button
                                                                        v-if="canRemoveEffort(effort)"
                                                                        type="button"
                                                                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        @click="confirmRemoveEffort(index, effort)"
                                                                    >
                                                                        Löschen
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="px-8 py-8 text-sm text-gray-500">
                                    Keine relevanten Kostenfreigaben vorhanden.
                                </div>
                            </div>
                        </div>
                        <div v-if="activeTab === 'actions'">
                            <div class="w-full">
                                <div class="sticky top-0 z-40 bg-white border-b border-bordergray-100 p-8">
                                    <div class="flex items-center justify-between gap-4">
                                        <div class="flex-1 min-w-0">
                                            <approval-add-bar
                                                label="Aktion"
                                                mode="input"
                                                :value="newActionApprovalTitle"
                                                placeholder="Titel der Aktionsfreigabe"
                                                :disabled="!documentation.can_edit"
                                                :can-add="canAddActionApproval"
                                                @input="newActionApprovalTitle = $event"
                                                @add="addActionApproval"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div v-if="(documentation.action_approvals || []).length > 0" class="px-8 pt-6 pb-8">
                                    <div class="space-y-5">
                                        <div
                                            v-for="(item, index) in documentation.action_approvals"
                                            :key="'manage-position-action-approval-' + (item.id || ('new-' + index))"
                                            class="rounded border border-bordergray-100 overflow-visible bg-white"
                                        >
                                            <div class="flex">
                                                <div :class="actionApprovalStatusStripClass(item)" class="w-1 flex-shrink-0"></div>

                                                <div class="flex-1 min-w-0">
                                                    <div class="px-6 py-6">
                                                        <label class="stage-filter-label block mb-1">
                                                            Titel
                                                        </label>
                                                        <input
                                                            :value="item.title"
                                                            :readonly="isActionApprovalLockedForCurrentUser(item)"
                                                            class="stage-filter-input form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            type="text"
                                                            @input="setActionApprovalField(index, 'title', $event.target.value)"
                                                        >

                                                        <div class="mt-5">
                                                            <label class="stage-filter-label block mb-1">
                                                                Kurzbeschreibung (Kundenansicht)
                                                            </label>
                                                            <textarea
                                                                v-model="documentation.action_approvals[index].short_description"
                                                                :readonly="isActionApprovalLockedForCurrentUser(item)"
                                                                class="stage-filter-input form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                                rows="3"
                                                            ></textarea>
                                                            <div
                                                                v-if="hasShortDescriptionUpdateMeta(item)"
                                                                class="mt-2 flex items-center text-xs text-gray-500"
                                                            >
                                                                <img
                                                                    :alt="(item.short_description_updated_by && item.short_description_updated_by.name) || 'User'"
                                                                    :src="getUserAvatarUrl(item.short_description_updated_by)"
                                                                    @error="handleAvatarImageError($event, item.short_description_updated_by)"
                                                                    class="h-5 w-5 rounded-full object-cover mr-2"
                                                                >
                                                                <span>{{ formatBodyUpdateMeta(item.short_description_updated_at, item.short_description_updated_by) }}</span>
                                                            </div>
                                                        </div>

                                                        <div v-if="item.approved_by && item.approved_by.name" class="mt-5">
                                                            <label class="stage-filter-label block mb-1">
                                                                Freigabe von
                                                            </label>
                                                            <div class="stage-form-control-inactive border rounded px-3 py-2">
                                                                <div class="flex items-center py-2">
                                                                    <div class="h-8 w-8 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                                        <img
                                                                            :alt="item.approved_by.name || 'Freigabe'"
                                                                            :src="getApprovalAvatarUrl(item.approved_by)"
                                                                            @error="handleApprovalAvatarImageError($event, item.approved_by)"
                                                                            class="h-full w-full object-cover"
                                                                        >
                                                                    </div>
                                                                    <div class="ml-4 min-w-0">
                                                                        <div class="text-sm text-gray-800">{{ item.approved_by.name }}</div>
                                                                        <div v-if="item.approved_at" class="text-xs text-gray-500">
                                                                            {{ getApprovalApprovedAtDateTimeText(item) }}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="stage-footer-surface border-t border-bordergray-100 px-4 py-0 flex items-stretch justify-between">
                                                        <div class="flex items-center py-4 pr-4">
                                                            <span class="inline-flex items-center text-sm font-medium text-gray-700">
                                                                <span
                                                                    class="inline-block h-2 w-2 rounded-full mr-2"
                                                                    :class="isActionApprovalCancelled(item) ? 'bg-red-500' : (isActionApprovalApproved(item) ? 'bg-green-500' : 'bg-gray-400')"
                                                                ></span>
                                                                {{ actionApprovalStatusLabel(item) }}
                                                            </span>
                                                        </div>
                                                        <div class="flex items-stretch">
                                                            <div v-if="isActionApprovalApproved(item)" class="flex items-center px-4 text-sm text-gray-500">
                                                                <span class="inline-flex items-center">
                                                                    <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.lock-alt-regular"></svg-vue>
                                                                    Freigegebene Aktionsfreigaben sind für die Bearbeitung gesperrt
                                                                </span>
                                                            </div>
                                                            <div v-if="isEmployee && isActionApprovalCheckedIn(item)" class="flex items-center px-4 text-sm text-gray-500">
                                                                <span class="inline-flex items-center">
                                                                    <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.lock-alt-regular"></svg-vue>
                                                                    Eingecheckte Leistungen sind für die Bearbeitung gesperrt.
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="!isActionApprovalApproved(item) && !isActionApprovalCancelled(item)"
                                                                :class="isEmployee ? 'w-auto border-l border-bordergray-100 flex items-center pl-4' : 'min-w-[170px] border-l border-bordergray-100 flex items-stretch'"
                                                            >
                                                                <select
                                                                    v-if="canManageActionApprovalCheckState(item)"
                                                                    :value="isActionApprovalCheckedIn(item) ? 'checked_in' : 'checked_out'"
                                                                    :disabled="isActionApprovalCheckStateSelectionDisabled(item)"
                                                                    :style="checkStateSelectionStyle(isActionApprovalCheckStateSelectionDisabled(item))"
                                                                    :class="[
                                                                        'form-select w-full h-full px-4 text-sm border-0 bg-transparent focus:ring-0 focus:border-transparent disabled:opacity-100',
                                                                        isActionApprovalCheckStateSelectionDisabled(item)
                                                                            ? 'text-gray-400 cursor-default'
                                                                            : 'text-gray-700'
                                                                    ]"
                                                                    @change="setActionApprovalCheckState(index, $event.target.value === 'checked_in')"
                                                                >
                                                                    <option value="checked_in">Eingecheckt</option>
                                                                    <option value="checked_out">Ausgecheckt</option>
                                                                </select>
                                                                <span v-else class="inline-flex items-center py-4 pr-4 text-sm text-gray-600 whitespace-nowrap">
                                                                    {{ isActionApprovalCheckedIn(item) ? 'Eingecheckt' : 'Ausgecheckt' }}
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="isEmployee && canRemoveActionApproval(item)"
                                                                :class="['flex items-center pl-4', isActionApprovalApproved(item) ? '' : 'border-l border-bordergray-100']"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    class="inline-flex items-center justify-center p-1 text-gray-700 cursor-pointer"
                                                                    @click="confirmRemoveActionApproval(index, item)"
                                                                >
                                                                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                                </button>
                                                            </div>
                                                            <div
                                                                v-else-if="canShowActionApprovalActionsMenu(item)"
                                                                v-on-clickaway="closeActionApprovalActionsMenu"
                                                                :class="['relative flex items-center pl-4', isActionApprovalApproved(item) ? '' : 'border-l border-bordergray-100']"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    class="inline-flex items-center justify-center p-1 text-gray-700 cursor-pointer"
                                                                    @click.stop="toggleActionApprovalActionsMenu(index, item)"
                                                                >
                                                                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                                        <circle cx="12" cy="5" r="1.8"></circle>
                                                                        <circle cx="12" cy="12" r="1.8"></circle>
                                                                        <circle cx="12" cy="19" r="1.8"></circle>
                                                                    </svg>
                                                                </button>
                                                                <div
                                                                    v-show="openActionApprovalActionsMenuKey === getActionApprovalActionsMenuKey(index, item)"
                                                                    class="origin-bottom-right absolute right-0 bottom-full mb-1 w-48 rounded shadow-lg z-50 bg-white border border-bordergray-100"
                                                                    @click.stop
                                                                >
                                                                    <button
                                                                        v-if="isActionApprovalApproved(item)"
                                                                        type="button"
                                                                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        @click="confirmCancelActionApproval(index, item)"
                                                                    >
                                                                        Stornieren
                                                                    </button>
                                                                    <button
                                                                        v-if="canRemoveActionApproval(item)"
                                                                        type="button"
                                                                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                        @click="confirmRemoveActionApproval(index, item)"
                                                                    >
                                                                        Löschen
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="activeTab === 'steps'">
                            <div
                                v-for="(processStep, index) in documentation.process_steps"
                                :key="'manage-position-step-editor-' + processStep.id"
                                class="flex items-stretch pb-3 last:pb-0"
                            >
                                <div class="relative w-7 mr-4 flex-shrink-0">
                                    <span
                                        class="absolute top-5 left-1/2 z-10 -translate-x-1/2 inline-block h-3 w-3 rounded-full"
                                        :style="getProcessStepTimelineDotStyle(processStep.color)"
                                    ></span>
                                    <span
                                        v-if="index < (documentation.process_steps.length - 1)"
                                        class="absolute left-1/2 z-0 -translate-x-1/2 w-px bg-bordergray-100"
                                        style="top: 1.625rem; bottom: -2rem;"
                                    ></span>
                                </div>
                                <div
                                    class="flex-1 rounded border border-bordergray-100 bg-white overflow-hidden"
                                    :class="processStep.is_completed ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300'"
                                >
                                    <button
                                        v-if="!isMandatoryBoundaryProcessStep(processStep)"
                                        type="button"
                                        class="w-full py-3 pl-4 pr-6 flex items-center justify-between text-left"
                                        :disabled="!documentation.can_edit"
                                        @click="toggleStepEditor(processStep.id)"
                                    >
                                        <div class="min-w-0">
                                            <div class="text-base font-semibold text-gray-900">{{ processStep.name }}</div>
                                            <div v-if="processStep.short_description" class="text-sm text-gray-500 truncate">
                                                {{ processStep.short_description }}
                                            </div>
                                        </div>
                                        <div class="ml-4 text-sm text-gray-500 inline-flex items-center flex-shrink-0">
                                            <svg
                                                v-if="isStepOverdue(processStep)"
                                                class="h-5 w-5 mr-2 text-red-500"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.72 3h16.92a2 2 0 0 0 1.72-3l-8.46-14.14a2 2 0 0 0-3.43 0z"/>
                                                <path d="M12 9v4"/>
                                                <circle cx="12" cy="17" r="1"/>
                                            </svg>
                                            <span v-if="processStepDateRange(processStep)" class="mr-3">{{ processStepDateRange(processStep) }}</span>
                                            <svg
                                                class="h-4 w-4"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    v-if="activeStepEditorId === processStep.id"
                                                    d="M18 15l-6-6-6 6"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                                <path
                                                    v-else
                                                    d="M6 9l6 6 6-6"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    </button>
                                    <div
                                        v-else
                                        class="w-full py-3 pl-4 pr-6 flex items-center justify-between text-left"
                                    >
                                        <div class="min-w-0">
                                            <div class="text-base font-semibold text-gray-900">{{ processStep.name }}</div>
                                            <div v-if="processStep.short_description" class="text-sm text-gray-500 truncate">
                                                {{ processStep.short_description }}
                                            </div>
                                        </div>
                                        <div class="ml-4 text-sm text-gray-500 inline-flex items-center flex-shrink-0">
                                            <svg
                                                v-if="isStepOverdue(processStep)"
                                                class="h-5 w-5 mr-2 text-red-500"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.72 3h16.92a2 2 0 0 0 1.72-3l-8.46-14.14a2 2 0 0 0-3.43 0z"/>
                                                <path d="M12 9v4"/>
                                                <circle cx="12" cy="17" r="1"/>
                                            </svg>
                                            <span v-if="processStepDateRange(processStep)">{{ processStepDateRange(processStep) }}</span>
                                        </div>
                                    </div>
                                    <div v-if="activeStepEditorId === processStep.id && !isMandatoryBoundaryProcessStep(processStep)" class="border-t border-bordergray-100 py-6 pl-4 pr-6">
                                        <div class="text-xs tracking-wide text-gray-500 mb-2">Interne Anmerkung</div>
                                        <input-wysiwyg
                                            :id="'position_step_body_' + processStep.id"
                                            v-model="processStep.body"
                                            :readonly="!documentation.can_edit"
                                            :plugins="{images: true, cannedReply: false, attachment: false, shortCode: false, menuPoint: false}"
                                    >
                                        <template v-slot:top>
                                            <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                                <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                                </div>
                                            </template>
                                        </input-wysiwyg>
                                        <div
                                            v-if="hasStepBody(processStep) && processStep.body_updated_at && processStep.body_updated_by"
                                            class="mt-2 flex items-center text-xs text-gray-500"
                                        >
                                            <img
                                                :alt="processStep.body_updated_by.name || 'User'"
                                                :src="getUserAvatarUrl(processStep.body_updated_by)"
                                                @error="handleAvatarImageError($event, processStep.body_updated_by)"
                                                class="h-5 w-5 rounded-full object-cover mr-2"
                                            >
                                            <span>{{ formatBodyUpdateMeta(processStep.body_updated_at, processStep.body_updated_by) }}</span>
                                        </div>
                                    </div>
                                    <div class="border-t border-bordergray-100 py-6 pl-4 pr-6 flex items-center justify-between">
                                        <label class="inline-flex items-center text-sm text-gray-700">
                                        <input
                                            v-model="processStep.is_completed"
                                            :disabled="!documentation.can_edit"
                                            class="form-checkbox h-4 w-4 text-gray-700"
                                            type="checkbox"
                                            @change="onProcessStepCompletionChange(processStep)"
                                        >
                                        <span class="ml-2">Prozessschritt abgeschlossen</span>
                                        <span
                                            v-if="processStep.is_completed && processStep.completed_at"
                                            class="ml-1 text-gray-400"
                                        >
                                            ({{ formatProcessStepCompletedDate(processStep.completed_at) }})
                                        </span>
                                    </label>
                                        <div class="flex items-center">
                                            <template v-for="(reader, readerIndex) in getProcessStepReaders(processStep)" :key="'process-step-reader-' + processStep.id + '-' + reader.id">
                                                <img
                                                   
                                                    :alt="reader.name"
                                                    :src="getUserAvatarUrl(reader)"
                                                    @error="handleAvatarImageError($event, reader)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover"
                                                    :class="readerIndex > 0 ? '-ml-2' : ''"
                                                >
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="documentation.process_steps.length < 1" class="rounded border border-bordergray-100 bg-gray-50 p-4 text-sm text-gray-600">
                                Keine Prozessschritte konfiguriert. Bitte über den Einstellungen-Button im Header konfigurieren.
                            </div>
                        </div>
                        </div>
                        </div>
                        <aside v-if="hasAnyApprovals" class="w-80 min-w-[20rem] flex-shrink-0 border-l border-bordergray-100 bg-white flex flex-col min-h-0">
                            <div class="px-5 py-5">
                                <h3 class="text-xl font-semibold text-gray-900">Freigaben</h3>
                            </div>
                            <div class="flex-1 min-h-0 overflow-y-auto">
                                <approval-sidebar-list
                                    title="Kostenfreigaben"
                                    :entries="costApprovalEntries"
                                    :show-hours="true"
                                />
                                <approval-sidebar-list
                                    title="Aktionsfreigaben"
                                    :entries="actionApprovalEntries"
                                />
                            </div>
                        </aside>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <confirm-dialog
            :open="deletePositionModal"
            title="Position löschen"
            text="Soll diese Position wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden."
            confirm-label="Löschen"
            confirm-variant="danger"
            headline-id="delete-position-modal-headline"
            @close="deletePositionModal = false"
            @confirm="deletePosition"
        />
        <app-dialog
            :open="assignmentChangeModal.open"
            :title="$t('Change assignment').toString()"
            title-id="assignment-change-modal-headline"
            size="md"
            :busy="loading.assignment"
            @close="closeAssignmentChangeModal"
        >
                            <p v-if="getAssignmentChangeCurrentEmployee()" class="text-sm text-gray-500">
                                {{ $t('Current') }}: {{ getAssignmentChangeCurrentEmployee().name }}
                            </p>

                            <div class="mt-5 border border-bordergray-100 rounded bg-white overflow-hidden">
                                <label
                                    v-for="employee in getAssignmentChangeOptions(getAssignmentChangeEffort())"
                                    :key="'assignment-change-employee-' + employee.id"
                                    :class="[
                                        isAssignmentChangeEmployeeDisabled(getAssignmentChangeEffort(), employee)
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'cursor-pointer hover:bg-gray-50',
                                        'block border-b border-bordergray-100 last:border-b-0'
                                    ]"
                                >
                                    <div class="flex items-center px-4 py-3">
                                        <input
                                            v-model.number="assignmentChangeModal.selectedEmployeeId"
                                            :disabled="isAssignmentChangeEmployeeDisabled(getAssignmentChangeEffort(), employee)"
                                            :value="Number(employee.id)"
                                            class="form-radio h-4 w-4 text-gray-700 flex-shrink-0"
                                            name="assignment-change-employee"
                                            type="radio"
                                            @change="onAssignmentChangeEmployeeSelected(Number(employee.id))"
                                        >
                                        <div class="ml-3 h-7 w-7 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center flex-shrink-0">
                                            <img
                                                :alt="employee.name || 'Mitarbeiter'"
                                                :src="getEmployeeAvatarUrl(employee)"
                                                @error="handleAvatarImageError($event, employee)"
                                                class="h-full w-full object-cover"
                                            >
                                        </div>
                                        <div class="ml-3 min-w-0 flex-1">
                                            <div class="text-sm font-medium text-gray-800 truncate">{{ employee.name }}</div>
                                            <div class="text-xs text-gray-500 truncate">{{ getEmployeeAssignmentSubtitle(employee) }}</div>
                                            <div v-if="getAssignmentChangeEmployeeInvalidText(getAssignmentChangeEffort(), employee)" class="mt-1 text-xs text-red-600">
                                                {{ getAssignmentChangeEmployeeInvalidText(getAssignmentChangeEffort(), employee) }}
                                            </div>
                                        </div>
                                        <div class="ml-4 text-right text-sm text-gray-700 flex-shrink-0">
                                            <div>{{ getEmployeeProviderRateLabel(employee) }}</div>
                                        </div>
                                    </div>
                                </label>
                                <div
                                    v-if="getAssignmentChangeOptions(getAssignmentChangeEffort()).length < 1"
                                    class="px-4 py-4 text-sm text-gray-500"
                                >
                                    {{ $t('No alternative assignment available.') }}
                                </div>
                            </div>

                            <div v-if="getAssignmentChangeSelectedEmployee()" class="mt-5">
                                <label class="stage-filter-label block mb-1">Dienstleister-Leistungskonto</label>
                                <input-select
                                    :value="assignmentChangeModal.selectedProviderServiceAccountId"
                                    :options="getEmployeeProviderServiceAccountOptions(getAssignmentChangeSelectedEmployee())"
                                    :searchable="getEmployeeProviderServiceAccountOptions(getAssignmentChangeSelectedEmployee()).length > 6"
                                    class="stage-filter-select"
                                    option-label="name"
                                    required
                                    @input="setAssignmentChangeProviderServiceAccount"
                                >
                                    <template v-slot:selectedOption="props">
                                        <template v-if="!props || !props.anySelected">
                                            <span class="block truncate">Leistungskonto auswählen</span>
                                        </template>
                                        <template v-else>
                                            <div class="flex items-center space-x-3 min-w-0">
                                                <img
                                                    :src="resolveProviderServiceAccountAvatar(props.option, getAssignmentChangeEffort())"
                                                    :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                    class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                                >
                                                <div class="min-w-0">
                                                    <span class="block truncate">{{ props.option ? props.option.name : 'Leistungskonto auswählen' }}</span>
                                                    <span v-if="props.option" class="block truncate text-xs text-gray-500">{{ providerServiceAccountSubtitle(props.option) }}</span>
                                                </div>
                                            </div>
                                        </template>
                                    </template>
                                    <template v-slot:selectOption="props">
                                        <div class="flex items-center space-x-3 min-w-0">
                                            <img
                                                :src="resolveProviderServiceAccountAvatar(props.option, getAssignmentChangeEffort())"
                                                :alt="props.option && props.option.name ? props.option.name : 'Leistungskonto'"
                                                class="h-6 w-6 rounded-sm object-cover flex-shrink-0"
                                            >
                                            <div class="min-w-0 font-normal">
                                                <span class="block truncate">{{ props.option ? props.option.name : '' }}</span>
                                                <span v-if="props.option" class="block truncate text-xs text-gray-500">{{ providerServiceAccountSubtitle(props.option) }}</span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:notFound>
                                        <div class="font-normal block truncate">Keine Leistungskonten verfügbar</div>
                                    </template>
                                </input-select>
                            </div>
            <template #footer>
                <app-dialog-action
                    :label="$t('Save').toString()"
                    :disabled="!canSaveAssignmentChange() || loading.assignment"
                    :loading="loading.assignment"
                    @click="saveAssignmentChange"
                />
            </template>
        </app-dialog>
        <confirm-dialog
            :open="cancelApprovalModal.open"
            :title="getCancelApprovalModalTitle()"
            :text="getCancelApprovalModalText()"
            confirm-label="Stornieren"
            confirm-variant="danger"
            headline-id="cancel-approval-modal-headline"
            @close="closeCancelApprovalModal"
            @confirm="confirmCancelApproval"
        />
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import ApprovalAddBar from "@/components/elements/position/approval-add-bar.vue";
import ApprovalSidebarList from "@/components/elements/position/approval-sidebar-list.vue";
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveAccessState} from "@/utils/access-state";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {UI_COLORS} from '@/utils/ui-colors';
import {formatCurrency as formatFinanceCurrency, formatSignedCurrency as formatFinanceSignedCurrency} from "@/utils/workspace-finance-settings";
import {
    applyApprovalAvatarImageFallback,
    applyAvatarImageFallback,
    formatBodyUpdateMeta,
    formatPositionStepDate,
    formatStepBodyUpdatedAt,
    getApprovalApprovedAtDateTimeText,
    getApprovalApprovedAtText,
    getApprovalCheckinOrDraftText,
    getApprovalEffortHoursText,
    getApprovalStatusBorderColor,
    getApprovalStatusLabel,
    getApprovalStatusText,
    getAssignedEmployeesByIds,
    getEmployeeAssignmentSubtitle,
    getPositionApprovalAvatarUrl,
    getPositionManageTabStorageKey,
    getPositionProjectImageUrl,
    getPositionProjectTooltip,
    getPositionEmployeeAvatarUrl,
    getPositionUserAvatarUrl,
    getProcessStepTimelineDotStyle,
    getUniqueUsersById as getUniquePositionUsersById,
    hasStepBody,
    isAllowedPositionManageTab,
    isPositionStepOverdue,
    normalizeAssignedCompany as normalizePositionAssignedCompany,
    normalizeHoursForPayload as normalizePositionHoursForPayload,
    normalizePositionProjectIds,
    normalizeQuantificationFactorForPayload as normalizePositionQuantificationFactorForPayload,
    normalizeQuantificationValueForPayload as normalizePositionQuantificationValueForPayload,
    normalizeRateForPayload as normalizePositionRateForPayload,
    persistPositionManageTab,
    restorePositionManageTab,
    withOpacity,
} from "@/utils/workspace-position-view";

export default {
    name: "position-manage",
    mixins: [clickaway],
    components: {
        ApprovalAddBar,
        ApprovalSidebarList,
        AppDialog,
        AppDialogAction,
        ConfirmDialog,
    },
    metaInfo() {
        return {
            title: "Position bearbeiten"
        }
    },
    data() {
        return {
            loading: {
                initial: false,
                saving: false,
                deleting: false,
                file: false,
                assignment: false,
            },
            activeTab: 'steps',
            activeStepEditorId: null,
            deletePositionModal: false,
            cancelApprovalModal: {
                open: false,
                type: null,
                index: null,
                title: '',
            },
            assignmentChangeModal: {
                open: false,
                index: null,
                effortId: null,
                title: '',
                selectedEmployeeId: null,
                selectedProviderServiceAccountId: null,
            },
            uploadingFileProgress: 0,
            projectDropdownOpen: false,
            headerActionsOpen: false,
            openEffortActionsMenuKey: null,
            openActionApprovalActionsMenuKey: null,
            newEffortServiceCategoryId: null,
            newActionApprovalTitle: '',
            selectedProjectIds: [],
            documentation: {
                title: '',
                customer_ticket_reference: null,
                employee_ticket_reference: null,
                gitlab_reference: null,
                body: '',
                body_updated_at: null,
                body_updated_by: null,
                requires_customer_cost_approval: false,
                customer_position_description: '',
                customer_cost_description: '',
                customer_action_description: '',
                customer_cost_approval_status: 'pending',
                customer_cost_approval_note: null,
                customer_cost_approved_at: null,
                efforts: [],
                action_approvals: [],
                effort_catalog: [],
                process_steps: [],
                process_step_ids: [],
                current_process_step_id: null,
                project_id: null,
                available_projects: [],
                readers: [],
                company_id: null,
                company: null,
                operator_company: null,
                attachments: [],
                can_edit: false,
                can_manage_permissions: false,
                can_delete: false,
                delete_block_reason: null,
                user: null,
            }
        };
    },
    computed: {
        accessState() {
            return resolveAccessState(this.$store);
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isCustomerPositionRoute() {
            return String(this.$route.path || '').startsWith('/positions');
        },
        positionsListPath() {
            const fallbackPath = this.isCustomerPositionRoute ? '/positions/list' : '/workspace/positions';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
        isEmployee() {
            return this.accessState.isEmployeeLike;
        },
        showHeaderProjectCell() {
            return this.isAdmin || this.isEmployee;
        },
        currentUserId() {
            const user = this.$store.state.user || {};
            const id = Number(user.id || 0);
            return id > 0 ? id : null;
        },
        hasAssignedCompany() {
            return !!(this.documentation.company && Number(this.documentation.company.id || 0) > 0);
        },
        assignedCompany() {
            return this.hasAssignedCompany ? this.documentation.company : null;
        },
        hasAnyApprovals() {
            const hasEfforts = this.visibleEffortsWithIndex.length > 0;
            const hasActions = Array.isArray(this.documentation.action_approvals) && this.documentation.action_approvals.length > 0;
            return hasEfforts || hasActions;
        },
        visibleEffortsWithIndex() {
            const efforts = Array.isArray(this.documentation.efforts) ? this.documentation.efforts : [];

            return efforts
                .map((effort, index) => ({effort, index}))
                .filter(({effort}) => {
                    if (!this.isEmployee) {
                        return true;
                    }

                    return this.isEffortAssignedToCurrentUser(effort);
                });
        },
        hasProjectBodyMeta() {
            return String(this.documentation.body || '').trim() !== ''
                && !!this.documentation.body_updated_at
                && !!this.documentation.body_updated_by;
        },
        availableProjects() {
            const collection = this.documentation && this.documentation.available_projects
                ? this.documentation.available_projects
                : [];
            if (Array.isArray(collection)) {
                return collection;
            }
            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }
            return [];
        },
        assignedProjectsForHeader() {
            const assignedProjects = Array.isArray(this.documentation?.projects)
                ? this.documentation.projects
                : (this.documentation?.projects && Array.isArray(this.documentation.projects.data) ? this.documentation.projects.data : []);
            const normalizedAssignedProjects = assignedProjects
                .filter((project) => project && Number(project.id) > 0);

            if (normalizedAssignedProjects.length > 0) {
                return normalizedAssignedProjects;
            }

            if (this.documentation?.project && Number(this.documentation.project.id) > 0) {
                return [this.documentation.project];
            }

            const selectedIds = this.normalizeProjectIds(this.documentation);
            if (selectedIds.length < 1) {
                return [];
            }

            const availableById = new Map(
                (this.availableProjects || [])
                    .filter((project) => project && Number(project.id) > 0)
                    .map((project) => [Number(project.id), project])
            );

            return selectedIds
                .map((projectId) => availableById.get(Number(projectId)))
                .filter((project) => !!project);
        },
        costApprovalEntries() {
            return this.visibleEffortsWithIndex.map(({effort, index: effortIndex}) => ({
                key: 'approval-effort-item-' + (effort.id || ('new-' + effortIndex)),
                status: effort.status || 'pending',
                title: this.getEffortCategoryName(effort.service_category_id),
                hoursText: this.getApprovalEffortHoursText(effort),
                requestedText: this.getApprovalCheckinOrDraftText(effort),
                approvedText: this.getApprovalApprovedAtDateTimeText(effort),
                borderColor: this.getApprovalStatusBorderColor(effort.status),
            }));
        },
        actionApprovalEntries() {
            return (this.documentation.action_approvals || []).map((item, itemIndex) => ({
                key: 'approval-action-item-' + (item.id || ('new-' + itemIndex)),
                status: item.status || 'pending',
                title: item.title || 'Aktionsfreigabe',
                requestedText: this.getApprovalCheckinOrDraftText(item),
                approvedText: this.getApprovalApprovedAtDateTimeText(item),
                borderColor: this.getApprovalStatusBorderColor(item.status),
            }));
        },
        approvalUsers() {
            const users = {};
            const ownerId = this.documentation.user ? this.documentation.user.id : null;
            const addOrUpdate = (id, name, canRead, canEdit) => {
                if (!users[id]) {
                    users[id] = {
                        id: id,
                        name: name,
                        role: '',
                        can_read: false,
                        can_edit: false,
                    };
                }
                users[id].can_read = users[id].can_read || canRead;
                users[id].can_edit = users[id].can_edit || canEdit;
            };

            (this.documentation.readers || []).forEach((reader) => {
                if (ownerId !== null && Number(reader.id) === Number(ownerId)) {
                    return;
                }
                addOrUpdate(reader.id, reader.name, true, true);
                if (!users[reader.id].role) {
                    users[reader.id].role = 'Mitarbeiter';
                }
            });

            if (this.assignedCompany) {
                const companyId = 1000000 + Number(this.assignedCompany.id);
                addOrUpdate(companyId, this.assignedCompany.name, true, false);
                users[companyId].role = 'Unternehmen';
            }

            return Object.values(users);
        },
        costApprovalDotClass() {
            if (!this.documentation.requires_customer_cost_approval) {
                return 'bg-gray-400';
            }
            return this.documentation.customer_cost_approval_status === 'approved' ? 'bg-green-500' : 'bg-red-500';
        },
        costApprovalLabel() {
            if (!this.documentation.requires_customer_cost_approval) {
                return 'Nicht erforderlich';
            }
            return this.documentation.customer_cost_approval_status === 'approved' ? 'Freigegeben' : 'Ausstehend';
        },
        costApprovalSettingsLocked() {
            return !!this.documentation.requires_customer_cost_approval
                && this.documentation.customer_cost_approval_status === 'approved';
        },
        canAddEffort() {
            if (!this.documentation.can_edit) {
                return false;
            }
            return Number(this.newEffortServiceCategoryId || 0) > 0;
        },
        canAddActionApproval() {
            if (!this.documentation.can_edit) {
                return false;
            }
            return (this.newActionApprovalTitle || '').trim().length > 0;
        },
    },
    watch: {
        activeTab(newValue) {
            this.persistActiveTab(newValue);
        },
    },
    mounted() {
        this.restoreActiveTab();
        this.getFilters();
        this.getDocumentation();
    },
    methods: {
        getActiveTabStorageKey() {
            return getPositionManageTabStorageKey(this.$route.params.id);
        },
        isAllowedActiveTab(tab) {
            return isAllowedPositionManageTab(tab);
        },
        persistActiveTab(tab) {
            persistPositionManageTab(this.$route.params.id, tab);
        },
        restoreActiveTab() {
            const savedTab = restorePositionManageTab(this.$route.params.id);
            if (savedTab) {
                this.activeTab = savedTab;
            }
        },
        closeProjectDropdown() {
            this.projectDropdownOpen = false;
        },
        toggleProjectDropdown() {
            this.projectDropdownOpen = !this.projectDropdownOpen;
            if (this.projectDropdownOpen) {
                this.headerActionsOpen = false;
            }
        },
        closeHeaderActionsMenu() {
            this.headerActionsOpen = false;
        },
        toggleHeaderActionsMenu() {
            this.headerActionsOpen = !this.headerActionsOpen;
            if (this.headerActionsOpen) {
                this.projectDropdownOpen = false;
            }
        },
        getEffortActionsMenuKey(index, effort) {
            return `effort:${effort && effort.id ? effort.id : index}`;
        },
        toggleEffortActionsMenu(index, effort) {
            const key = this.getEffortActionsMenuKey(index, effort);
            this.openEffortActionsMenuKey = this.openEffortActionsMenuKey === key ? null : key;
        },
        closeEffortActionsMenu() {
            this.openEffortActionsMenuKey = null;
        },
        confirmCancelEffort(index, effort) {
            if (!this.isAdmin || !this.isEffortApproved(effort)) {
                return;
            }
            this.openCancelApprovalModal('effort', index, this.getEffortCategoryName(effort.service_category_id));
            this.openEffortActionsMenuKey = null;
        },
        openAssignmentChangeModal(index, effort) {
            if (!this.canChangeEffortAssignment(effort)) {
                return;
            }

            const options = this.getAssignmentChangeOptions(effort)
                .filter((employee) => !this.isAssignmentChangeEmployeeDisabled(effort, employee));
            const selectedEmployee = options[0] || null;
            const selectedAccountId = selectedEmployee
                ? this.getDefaultProviderServiceAccountId(selectedEmployee)
                : null;
            this.assignmentChangeModal = {
                open: true,
                index: index,
                effortId: effort && effort.id ? Number(effort.id) : null,
                title: this.getEffortCategoryName(effort.service_category_id),
                selectedEmployeeId: selectedEmployee ? Number(selectedEmployee.id) : null,
                selectedProviderServiceAccountId: selectedAccountId,
            };
            this.openEffortActionsMenuKey = null;
        },
        closeAssignmentChangeModal() {
            if (this.loading.assignment) {
                return;
            }

            this.assignmentChangeModal = {
                open: false,
                index: null,
                effortId: null,
                title: '',
                selectedEmployeeId: null,
                selectedProviderServiceAccountId: null,
            };
        },
        getAssignmentChangeEffort() {
            const index = Number(this.assignmentChangeModal.index);
            if (Number.isNaN(index) || index < 0) {
                return null;
            }

            return (this.documentation.efforts || [])[index] || null;
        },
        getAssignmentChangeCurrentEmployee() {
            const effort = this.getAssignmentChangeEffort();
            return this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
        },
        getAssignmentChangeSelectedEmployee() {
            return this.getEffortEmployeeOptionById(this.assignmentChangeModal.selectedEmployeeId);
        },
        onAssignmentChangeEmployeeSelected(employeeId) {
            const employee = this.getEffortEmployeeOptionById(employeeId);
            this.assignmentChangeModal.selectedEmployeeId = employee ? Number(employee.id) : null;
            this.assignmentChangeModal.selectedProviderServiceAccountId = employee
                ? this.getDefaultProviderServiceAccountId(employee)
                : null;
        },
        setAssignmentChangeProviderServiceAccount(accountId) {
            const normalizedId = Number(accountId || 0);
            this.assignmentChangeModal.selectedProviderServiceAccountId = normalizedId > 0 ? normalizedId : null;
        },
        getAssignmentChangeOptions(effort) {
            const currentEmployeeId = this.getPrimaryEffortEmployeeId(effort);

            return this.getAdminAssignableEffortEmployees()
                .filter((employee) => {
                    if (!employee || employee.is_admin) {
                        return false;
                    }

                    const employeeId = Number(employee.id || 0);
                    return employeeId > 0 && employeeId !== Number(currentEmployeeId || 0);
                });
        },
        getEmployeeProviderServiceAccountOptions(employee) {
            if (!employee || !Array.isArray(employee.provider_service_accounts)) {
                return [];
            }

            return employee.provider_service_accounts
                .filter((account) => Number(account && account.id || 0) > 0)
                .map((account) => ({
                    ...account,
                    id: Number(account.id),
                }));
        },
        getDefaultProviderServiceAccountId(employee) {
            const accounts = this.getEmployeeProviderServiceAccountOptions(employee);
            return accounts.length === 1 ? Number(accounts[0].id) : null;
        },
        getEmployeeProviderServiceAccountById(employee, accountId) {
            const normalizedId = Number(accountId || 0);
            if (normalizedId < 1) {
                return null;
            }

            return this.getEmployeeProviderServiceAccountOptions(employee)
                .find((account) => Number(account.id) === normalizedId) || null;
        },
        getEmployeeProviderHourlyRate(employee, accountId = null) {
            const resolvedAccountId = Number(accountId || 0) > 0
                ? Number(accountId)
                : this.getDefaultProviderServiceAccountId(employee);
            const account = this.getEmployeeProviderServiceAccountById(employee, resolvedAccountId);
            if (!account || account.hourly_rate === null || typeof account.hourly_rate === 'undefined' || account.hourly_rate === '') {
                return null;
            }

            return this.normalizeRateForPayload(account.hourly_rate);
        },
        getEmployeeProviderRateLabel(employee) {
            const accounts = this.getEmployeeProviderServiceAccountOptions(employee);
            if (accounts.length > 1) {
                return `${accounts.length} Konten`;
            }

            const rate = this.getEmployeeProviderHourlyRate(employee);
            return rate === null ? '-' : `${this.formatOptionalCurrency(rate)} / Std.`;
        },
        getAssignmentChangeCustomerHourlyRate(effort) {
            const effortRate = this.normalizeRateForPayload(effort && effort.customer_hourly_rate);
            return effortRate !== null
                ? effortRate
                : this.getDefaultCustomerHourlyRateForEffort(effort);
        },
        getAssignmentChangeNetAmount(hours, hourlyRate, effort) {
            const normalizedHours = this.normalizeHoursForPayload(hours);
            const normalizedHourlyRate = this.normalizeRateForPayload(hourlyRate);
            if (normalizedHours === null || normalizedHourlyRate === null) {
                return null;
            }

            const baseAmount = Math.round(normalizedHours * normalizedHourlyRate * 100) / 100;
            const factor = this.getEffortQuantificationFactor(effort);
            const value = this.getEffortQuantificationValue(effort);

            return Math.round(baseAmount * (1 + (factor * value)) * 100) / 100;
        },
        getAssignmentChangeInternalNetAmount(effort, account) {
            return this.getAssignmentChangeNetAmount(
                effort && effort.internal_hours,
                account && account.hourly_rate,
                effort
            );
        },
        getAssignmentChangeCustomerNetAmount(effort) {
            return this.getAssignmentChangeNetAmount(
                effort && effort.customer_hours,
                this.getAssignmentChangeCustomerHourlyRate(effort),
                effort
            );
        },
        getProviderServiceAccountFinancialValidationText(effort, account) {
            const providerHourlyRate = this.normalizeRateForPayload(account && account.hourly_rate);
            if (providerHourlyRate === null || providerHourlyRate <= 0) {
                return this.$i18n.t('No valid hourly rate is configured.').toString();
            }

            const customerHourlyRate = this.getAssignmentChangeCustomerHourlyRate(effort);
            if (customerHourlyRate === null || customerHourlyRate <= 0) {
                return this.$i18n.t('No valid customer hourly rate is configured.').toString();
            }

            if (providerHourlyRate > (customerHourlyRate + 0.00001)) {
                return this.$i18n.t('Hourly rate exceeds the customer hourly rate.').toString();
            }

            const internalAmount = this.getAssignmentChangeInternalNetAmount(effort, account);
            const customerAmount = this.getAssignmentChangeCustomerNetAmount(effort);
            if (internalAmount === null || customerAmount === null) {
                return this.$i18n.t('Calculation is incomplete.').toString();
            }

            if (internalAmount > (customerAmount + 0.00001)) {
                return this.$i18n.t('Internal total exceeds the customer total.').toString();
            }

            return '';
        },
        getAssignmentChangeEmployeeInvalidText(effort, employee) {
            const accounts = this.getEmployeeProviderServiceAccountOptions(employee);
            if (accounts.length < 1) {
                return 'Kein aktives Dienstleister-Leistungskonto verfügbar.';
            }

            const hasValidAccount = accounts.some((account) => this.getProviderServiceAccountFinancialValidationText(effort, account) === '');
            return hasValidAccount
                ? ''
                : this.getProviderServiceAccountFinancialValidationText(effort, accounts[0]);
        },
        isAssignmentChangeEmployeeDisabled(effort, employee) {
            return this.getAssignmentChangeEmployeeInvalidText(effort, employee) !== '';
        },
        canSaveAssignmentChange() {
            const effort = this.getAssignmentChangeEffort();
            const selectedEmployeeId = Number(this.assignmentChangeModal.selectedEmployeeId || 0);
            if (!this.assignmentChangeModal.open || !effort || selectedEmployeeId < 1) {
                return false;
            }

            const employee = this.getAssignmentChangeOptions(effort)
                .find((item) => Number(item.id) === selectedEmployeeId);
            if (!employee || this.isAssignmentChangeEmployeeDisabled(effort, employee)) {
                return false;
            }

            const account = this.getEmployeeProviderServiceAccountById(
                employee,
                this.assignmentChangeModal.selectedProviderServiceAccountId
            );
            return !!account && this.getProviderServiceAccountFinancialValidationText(effort, account) === '';
        },
        confirmRemoveEffort(index, effort) {
            if (!this.canRemoveEffort(effort)) {
                return;
            }
            this.removeEffort(index);
            this.openEffortActionsMenuKey = null;
        },
        getActionApprovalActionsMenuKey(index, item) {
            return `action:${item && item.id ? item.id : index}`;
        },
        toggleActionApprovalActionsMenu(index, item) {
            const key = this.getActionApprovalActionsMenuKey(index, item);
            this.openActionApprovalActionsMenuKey = this.openActionApprovalActionsMenuKey === key ? null : key;
        },
        closeActionApprovalActionsMenu() {
            this.openActionApprovalActionsMenuKey = null;
        },
        confirmCancelActionApproval(index, item) {
            if (!this.isAdmin || !this.isActionApprovalApproved(item)) {
                return;
            }
            this.openCancelApprovalModal('action', index, item.title || 'Aktionsfreigabe');
            this.openActionApprovalActionsMenuKey = null;
        },
        confirmRemoveActionApproval(index, item) {
            if (!this.canRemoveActionApproval(item)) {
                return;
            }
            this.removeActionApproval(index);
            this.openActionApprovalActionsMenuKey = null;
        },
        openCancelApprovalModal(type, index, title) {
            this.cancelApprovalModal = {
                open: true,
                type: type,
                index: index,
                title: title || '',
            };
        },
        closeCancelApprovalModal() {
            this.cancelApprovalModal = {
                open: false,
                type: null,
                index: null,
                title: '',
            };
        },
        getCancelApprovalModalTitle() {
            if (this.cancelApprovalModal.type === 'effort') {
                return this.$t('Cancel cost approval').toString();
            }
            if (this.cancelApprovalModal.type === 'action') {
                return this.$t('Cancel action approval').toString();
            }
            return this.$t('Cancel approval').toString();
        },
        getCancelApprovalModalText() {
            const name = String(this.cancelApprovalModal.title || '').trim();
            if (name !== '') {
                return `Soll "${name}" wirklich storniert werden? Diese Aktion kann nicht rückgängig gemacht werden.`;
            }
            return this.$t('Do you really want to cancel this approval? This action cannot be undone.').toString();
        },
        confirmCancelApproval() {
            if (!this.cancelApprovalModal.open) {
                return;
            }

            const index = Number(this.cancelApprovalModal.index);
            if (Number.isNaN(index) || index < 0) {
                this.closeCancelApprovalModal();
                return;
            }

            if (this.cancelApprovalModal.type === 'effort') {
                this.cancelEffort(index);
            } else if (this.cancelApprovalModal.type === 'action') {
                this.cancelActionApproval(index);
            }

            this.closeCancelApprovalModal();
        },
        getFilters() {
            const self = this;
            axios.get('api/workspace/positions/filters').then(function (response) {
                self.documentation.effort_catalog = response.data.service_categories || [];
            });
        },
        applyPositionResponse(position) {
            this.documentation = position;
            this.deletePositionModal = false;
            this.closeCancelApprovalModal();
            if (this.assignmentChangeModal.open && !this.loading.assignment) {
                this.closeAssignmentChangeModal();
            }
            this.documentation.process_steps = this.documentation.process_steps || [];
            this.documentation.company = this.normalizeAssignedCompany(this.documentation.company);
            this.documentation.operator_company = this.normalizeAssignedCompany(this.documentation.operator_company);
            this.documentation.company_id = this.documentation.company
                ? Number(this.documentation.company.id)
                : (Number(this.documentation.company_id || 0) > 0 ? Number(this.documentation.company_id) : null);
            this.documentation.efforts = Array.isArray(this.documentation.efforts) ? this.documentation.efforts : [];
            this.documentation.action_approvals = Array.isArray(this.documentation.action_approvals) ? this.documentation.action_approvals : [];
            this.documentation.available_projects = Array.isArray(this.documentation.available_projects)
                ? this.documentation.available_projects
                : [];
            this.syncProjectSelection(this.documentation);
            this.documentation.employee_options = Array.isArray(this.documentation.employee_options)
                ? this.getUniqueUsersById(this.documentation.employee_options)
                : [];
            if (this.isEmployee && this.currentUserId) {
                this.documentation.efforts = this.documentation.efforts.map((effort) => {
                    const employeeIds = Array.isArray(effort && effort.employee_ids)
                        ? effort.employee_ids.map((id) => Number(id)).filter((id) => id > 0)
                        : [];

                    return {
                        ...effort,
                        employee_ids: employeeIds.length > 0 ? employeeIds : [Number(this.currentUserId)],
                    };
                });
            }
            this.documentation.efforts = this.documentation.efforts.map((effort) => {
                const status = String((effort && effort.status) || 'pending');
                if (['approved', 'cancelled'].includes(status)) {
                    return effort;
                }

                const employee_ids = this.resolveEffortEmployeeIdsForEditing(effort);
                const normalizedEffort = {
                    ...effort,
                    employee_ids,
                };

                return {
                    ...normalizedEffort,
                    provider_service_account_id: this.resolveProviderServiceAccountIdForEditing(normalizedEffort),
                    customer_hourly_rate: this.getDefaultCustomerHourlyRateForEffort(normalizedEffort),
                    quantification_factor: this.normalizeQuantificationFactorForPayload(effort.quantification_factor ?? 0),
                    quantification_value: this.normalizeQuantificationValueForPayload(effort.quantification_value ?? 0.25),
                };
            });
            this.newEffortServiceCategoryId = (this.documentation.effort_catalog || []).length > 0
                ? Number(this.documentation.effort_catalog[0].id)
                : null;
            const firstEditableStep = this.getFirstEditableStep();
            this.activeStepEditorId = firstEditableStep ? firstEditableStep.id : null;
        },
        getApiErrorMessage(error, fallbackText) {
            const fallback = fallbackText || this.$i18n.t('An error occurred while saving data').toString();
            if (!error || !error.response || !error.response.data) {
                return fallback;
            }

            const responseData = error.response.data;
            const fieldErrors = responseData.errors;
            if (fieldErrors && typeof fieldErrors === 'object') {
                const firstErrorList = Object.values(fieldErrors).find((messages) => Array.isArray(messages) && messages.length > 0);
                if (firstErrorList) {
                    const firstFieldError = String(firstErrorList[0] || '').trim();
                    if (firstFieldError !== '') {
                        return firstFieldError;
                    }
                }
            }

            if (!responseData.message) {
                return fallback;
            }

            const message = String(responseData.message).trim();
            if (message === '' || message === 'The given data was invalid.') {
                return fallback;
            }

            return message;
        },
        getDocumentation() {
            const self = this;
            self.loading.initial = true;
            axios.get('api/workspace/positions/' + self.$route.params.id).then(function (response) {
                self.applyPositionResponse(response.data);
                self.loading.initial = false;
            }).catch(function () {
                self.loading.initial = false;
                self.$router.push(self.positionsListPath);
            });
        },
        saveDocumentation() {
            const self = this;
            if (!self.documentation.can_edit || self.loading.saving) {
                return;
            }
            self.loading.saving = true;
            const payload = {
                title: self.documentation.title,
                customer_ticket_reference: self.documentation.customer_ticket_reference,
                employee_ticket_reference: self.documentation.employee_ticket_reference,
                gitlab_reference: self.documentation.gitlab_reference,
                body: self.documentation.body,
                requires_customer_cost_approval: self.isAdmin ? !!self.documentation.requires_customer_cost_approval : undefined,
                customer_position_description: self.isAdmin ? self.documentation.customer_position_description : undefined,
                customer_cost_description: self.isAdmin ? self.documentation.customer_cost_description : undefined,
                customer_action_description: self.isAdmin ? self.documentation.customer_action_description : undefined,
                efforts: (self.documentation.efforts || []).map((effort) => ({
                    id: effort.id || undefined,
                    service_category_id: Number(effort.service_category_id),
                    internal_hours: this.normalizeHoursForPayload(effort.internal_hours),
                    customer_hours: this.normalizeHoursForPayload(effort.customer_hours),
                    customer_hourly_rate: this.normalizeRateForPayload(effort.customer_hourly_rate),
                    quantification_factor: this.normalizeQuantificationFactorForPayload(effort.quantification_factor),
                    quantification_value: this.normalizeQuantificationValueForPayload(effort.quantification_value),
                    short_description: effort.short_description ?? null,
                    employee_ids: (effort.employee_ids || []).map((id) => Number(id)),
                    provider_service_account_id: effort.provider_service_account_id
                        ? Number(effort.provider_service_account_id)
                        : null,
                    is_checked_in: self.isAdmin ? !!effort.is_checked_in : undefined,
                    cancel: !!effort.cancel,
                })),
                action_approvals: (self.documentation.action_approvals || []).map((item) => ({
                    id: item.id || undefined,
                    title: item.title || '',
                    short_description: item.short_description ?? null,
                    is_checked_in: self.isAdmin ? !!item.is_checked_in : undefined,
                    cancel: !!item.cancel,
                })),
                attachments: self.documentation.attachments,
                process_step_ids: (self.documentation.process_steps || []).map((step) => step.id),
                current_process_step_id: self.documentation.current_process_step_id,
                process_steps: (self.documentation.process_steps || []).map((step) => ({
                    id: step.id,
                    start_date: self.isMandatoryBoundaryProcessStep(step)
                        ? (step.start_date || step.end_date || null)
                        : (step.start_date || null),
                    end_date: self.isMandatoryBoundaryProcessStep(step)
                        ? (step.start_date || step.end_date || null)
                        : (step.end_date || null),
                    body: self.isMandatoryBoundaryProcessStep(step) ? null : (step.body || null),
                    is_completed: !!step.is_completed,
                })),
            };
            axios.put('api/workspace/positions/' + self.$route.params.id, payload).then(function (response) {
                self.applyPositionResponse(response.data.position);
                self.loading.saving = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.loading.saving = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.getApiErrorMessage(error),
                    type: 'error'
                });
            });
        },
        saveAssignmentChange() {
            const self = this;
            if (!self.canSaveAssignmentChange() || self.loading.assignment) {
                return;
            }

            const effort = self.getAssignmentChangeEffort();
            const effortId = Number((effort && effort.id) || self.assignmentChangeModal.effortId || 0);
            const employeeId = Number(self.assignmentChangeModal.selectedEmployeeId || 0);
            if (effortId < 1 || employeeId < 1) {
                return;
            }

            self.loading.assignment = true;
            axios.post('api/workspace/positions/' + self.$route.params.id + '/cost-approvals/' + effortId + '/assignment', {
                employee_id: employeeId,
                provider_service_account_id: Number(self.assignmentChangeModal.selectedProviderServiceAccountId),
            }).then(function (response) {
                self.loading.assignment = false;
                self.closeAssignmentChangeModal();
                if (response.data && response.data.position) {
                    self.applyPositionResponse(response.data.position);
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: (response.data && response.data.message) || self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.loading.assignment = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.getApiErrorMessage(error),
                    type: 'error'
                });
            });
        },
        normalizeProjectIds(source) {
            return normalizePositionProjectIds(source);
        },
        getProjectImageUrl(project) {
            return getPositionProjectImageUrl(project);
        },
        getProjectTooltip(project) {
            return getPositionProjectTooltip(project);
        },
        syncProjectSelection(source) {
            this.selectedProjectIds = this.normalizeProjectIds(source);
        },
        mergeQuickActionPositionResponse(position) {
            if (!position || typeof position !== 'object') {
                return;
            }

            this.documentation.project = position.project || null;
            this.documentation.projects = Array.isArray(position.projects)
                ? position.projects
                : (position.projects && Array.isArray(position.projects.data) ? position.projects.data : []);
            this.documentation.available_projects = Array.isArray(position.available_projects)
                ? position.available_projects
                : (position.available_projects && Array.isArray(position.available_projects.data) ? position.available_projects.data : []);
            this.syncProjectSelection(position);
        },
        saveProjectAssignments() {
            const self = this;
            if (!self.isAdmin || !self.documentation.can_edit) {
                return;
            }

            axios.post('api/workspace/positions/' + self.$route.params.id + '/quick-actions', {
                action: 'project',
                value: self.normalizeProjectIds({project_ids: self.selectedProjectIds}),
            }).then(function (response) {
                self.mergeQuickActionPositionResponse(response.data.position);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.syncProjectSelection(self.documentation);
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.getApiErrorMessage(error),
                    type: 'error'
                });
            });
        },
        selectUploadFile() {
            if (!this.documentation.can_edit) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('No permission to edit').toString(),
                    type: 'warning'
                });
                return;
            }
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const file = e && e.target && e.target.files ? e.target.files[0] : null;
            if (!file) {
                self.$refs.fileInput.value = null;
                return;
            }
            if (!self.documentation.can_edit) {
                self.$refs.fileInput.value = null;
                return;
            }
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', file);
            axios.post(
                'api/workspace/positions/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.documentation.attachments.push(response.data);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: 'Datei hochgeladen. Sie ist im Tab "Projekt" sichtbar.',
                    type: 'success'
                });
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        removeAttachment(attachment) {
            if (!this.documentation.can_edit) {
                return;
            }
            this.documentation.attachments.splice(attachment, 1);
        },
        onProcessStepCompletionChange(processStep) {
            if (processStep.is_completed) {
                this.documentation.current_process_step_id = processStep.id;
            } else if (this.documentation.current_process_step_id === processStep.id) {
                this.documentation.current_process_step_id = null;
            }
        },
        getFirstEditableStep() {
            return (this.documentation.process_steps || [])[0] || null;
        },
        isMandatoryBoundaryProcessStep(processStep) {
            if (!processStep) {
                return false;
            }
            return !!processStep.is_mandatory || !!processStep.is_mandatory_start || !!processStep.is_mandatory_end;
        },
        processStepDateRange(processStep) {
            if (!processStep) {
                return '';
            }

            const startDate = processStep.start_date ? this.formatStepDate(processStep.start_date) : '';
            const endDate = processStep.end_date ? this.formatStepDate(processStep.end_date) : '';
            if (startDate && endDate && startDate === endDate) {
                return startDate;
            }
            if (startDate && endDate) {
                return `${startDate} - ${endDate}`;
            }
            return startDate || endDate || '';
        },
        normalizeHoursForPayload(value) {
            return normalizePositionHoursForPayload(value);
        },
        normalizeRateForPayload(value) {
            return normalizePositionRateForPayload(value);
        },
        normalizeQuantificationFactorForPayload(value) {
            return normalizePositionQuantificationFactorForPayload(value);
        },
        normalizeQuantificationValueForPayload(value) {
            return normalizePositionQuantificationValueForPayload(value);
        },
        getEffortQuantificationFactor(effort) {
            return this.normalizeQuantificationFactorForPayload(effort && effort.quantification_factor);
        },
        getEffortQuantificationValue(effort) {
            return this.normalizeQuantificationValueForPayload(effort && effort.quantification_value);
        },
        getEffortPreviewHours(effort) {
            if (this.isAdmin) {
                return this.normalizeHoursForPayload(effort && effort.customer_hours) || 0;
            }

            if (!this.canCurrentUserSeeEffortAmounts(effort)) {
                return 0;
            }

            return this.normalizeHoursForPayload(effort && effort.internal_hours) || 0;
        },
        getEffortPreviewHourlyRate(effort) {
            if (this.isAdmin) {
                const effortRate = this.normalizeRateForPayload(effort && effort.customer_hourly_rate);
                return effortRate !== null
                    ? effortRate
                    : (this.getDefaultCustomerHourlyRateForEffort(effort) || 0);
            }

            if (!this.canCurrentUserSeeEffortAmounts(effort)) {
                return 0;
            }

            return this.normalizeRateForPayload(this.getInternalHourlyRateForEffort(effort)) || 0;
        },
        getEffortPreviewBaseAmount(effort) {
            return Math.round(this.getEffortPreviewHours(effort) * this.getEffortPreviewHourlyRate(effort) * 100) / 100;
        },
        getEffortPreviewQuantificationFactor(effort) {
            if (!this.canCurrentUserSeeEffortAmounts(effort)) {
                return 0;
            }

            return this.getEffortQuantificationFactor(effort);
        },
        getEffortPreviewQuantificationValue(effort) {
            if (!this.canCurrentUserSeeEffortAmounts(effort)) {
                return 0;
            }

            return this.getEffortQuantificationValue(effort);
        },
        getEffortPreviewQuantificationAmount(effort) {
            const amount = this.getEffortPreviewBaseAmount(effort)
                * this.getEffortPreviewQuantificationFactor(effort)
                * this.getEffortPreviewQuantificationValue(effort);

            return Math.round(amount * 100) / 100;
        },
        shouldShowEffortPreviewQuantification(effort) {
            return this.getEffortPreviewQuantificationFactor(effort) > 0;
        },
        getEffortPreviewNetAmount(effort) {
            return Math.round((this.getEffortPreviewBaseAmount(effort) + this.getEffortPreviewQuantificationAmount(effort)) * 100) / 100;
        },
        getEffortPreviewVatRate(effort) {
            if (this.isAdmin) {
                const company = this.getAssignedCompanyOption();
                const rate = company
                    ? (company.customer_vat_rate ?? company.vat_rate ?? company.effective_customer_vat_rate)
                    : null;

                return this.normalizeRateForPayload(rate) || 0;
            }

            if (!this.canCurrentUserSeeEffortAmounts(effort)) {
                return 0;
            }

            const effortVatRate = this.normalizeRateForPayload(effort && effort.provider_vat_rate);
            const selectedAccount = this.getSelectedProviderServiceAccount(effort);
            const selectedAccountVatRate = this.normalizeRateForPayload(selectedAccount && selectedAccount.vat_rate);
            if (selectedAccountVatRate !== null) {
                return selectedAccountVatRate;
            }
            if (effortVatRate !== null) {
                return effortVatRate;
            }

            return this.getCurrentEmployeeVatRate(effort) || 0;
        },
        getEffortPreviewVatAmount(effort) {
            return Math.round((this.getEffortPreviewNetAmount(effort) * (this.getEffortPreviewVatRate(effort) / 100)) * 100) / 100;
        },
        getEffortPreviewGrossAmount(effort) {
            return Math.round((this.getEffortPreviewNetAmount(effort) + this.getEffortPreviewVatAmount(effort)) * 100) / 100;
        },
        normalizeAssignedCompany(company) {
            return normalizePositionAssignedCompany(company);
        },
        getAssignedCompanyOption() {
            return this.assignedCompany;
        },
        getDefaultCompanyHourlyRate() {
            const company = this.getAssignedCompanyOption();
            if (!company || company.customer_hourly_rate === null || typeof company.customer_hourly_rate === 'undefined') {
                return null;
            }

            return this.normalizeRateForPayload(company.customer_hourly_rate);
        },
        getDefaultCustomerHourlyRateForEffort(effort) {
            return this.getDefaultCompanyHourlyRate();
        },
        getReadonlyCustomerHourlyRateForEffort(effort) {
            const effortRate = this.normalizeRateForPayload(effort && effort.customer_hourly_rate);
            const rate = effortRate !== null
                ? effortRate
                : this.getDefaultCustomerHourlyRateForEffort(effort);
            if (rate === null) {
                return '';
            }

            return rate.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        getCurrentEmployeeHourlyRate(effort = null) {
            const employee = this.getEffortEmployeeOptionById(this.currentUserId);
            const account = effort ? this.getSelectedProviderServiceAccount(effort) : null;
            return this.getEmployeeProviderHourlyRate(employee, account && account.id);
        },
        getCurrentEmployeeVatRate(effort = null) {
            const account = effort ? this.getSelectedProviderServiceAccount(effort) : null;
            const employee = this.getEffortEmployeeOptionById(this.currentUserId);
            const selectedAccount = account || this.getEmployeeProviderServiceAccountById(
                employee,
                this.getDefaultProviderServiceAccountId(employee)
            );
            return this.normalizeRateForPayload(selectedAccount && selectedAccount.vat_rate);
        },
        getInternalHourlyRateForEffort(effort) {
            if (this.isAdminEffortSelection(effort)) {
                return null;
            }

            const selectedAccount = this.getSelectedProviderServiceAccount(effort);
            const selectedRate = this.normalizeRateForPayload(selectedAccount && selectedAccount.hourly_rate);
            if (selectedRate !== null) {
                return selectedRate;
            }

            const snapshotRate = this.normalizeRateForPayload(effort && effort.provider_hourly_rate);
            if (snapshotRate !== null) {
                return snapshotRate;
            }

            if (this.isEmployee) {
                return this.getCurrentEmployeeHourlyRate(effort);
            }

            const employeeIds = Array.isArray(effort && effort.employee_ids) ? effort.employee_ids : [];
            if (employeeIds.length < 1) {
                return null;
            }

            const employeeId = Number(employeeIds[0] || 0);
            if (employeeId < 1) {
                return null;
            }

            const employee = (this.documentation.employee_options || []).find((item) => Number(item.id) === employeeId);
            return this.getEmployeeProviderHourlyRate(employee, effort && effort.provider_service_account_id);
        },
        getAdminAssignableEffortEmployees() {
            const operatorCompanyId = Number((((this.documentation || {}).operator_company || {}).id) || 0);

            return this.getUniqueUsersById(this.documentation.employee_options || []).filter((employee) => {
                if (!employee) {
                    return false;
                }

                if (employee.is_admin) {
                    if (operatorCompanyId < 1) {
                        return false;
                    }

                    return Number(employee.provider_company_id || 0) === operatorCompanyId;
                }

                return Number(employee.provider_company_id || 0) > 0;
            });
        },
        getDefaultAdminEffortEmployeeId() {
            const firstEmployee = this.getAdminAssignableEffortEmployees()[0] || null;
            return firstEmployee ? Number(firstEmployee.id || 0) : 0;
        },
        resolveEffortEmployeeIdsForEditing(effort) {
            const employeeIds = Array.isArray(effort && effort.employee_ids)
                ? effort.employee_ids.map((id) => Number(id)).filter((id) => id > 0)
                : [];

            if (employeeIds.length > 0) {
                return [employeeIds[0]];
            }

            if (this.isEmployee && this.currentUserId) {
                return [Number(this.currentUserId)];
            }

            if (this.isAdmin) {
                const defaultEmployeeId = this.getDefaultAdminEffortEmployeeId();
                return defaultEmployeeId > 0 ? [defaultEmployeeId] : [];
            }

            return [];
        },
        getPrimaryEffortEmployeeId(effort) {
            const employeeIds = Array.isArray(effort && effort.employee_ids) ? effort.employee_ids : [];
            if (employeeIds.length < 1) {
                return 0;
            }

            return Number(employeeIds[0] || 0);
        },
        getEffortEmployeeOptionById(employeeId) {
            return this.getUniqueUsersById(this.documentation.employee_options || []).find((employee) => Number(employee.id) === Number(employeeId)) || null;
        },
        getEffortProviderServiceAccountOptions(effort) {
            const employee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            return this.getEmployeeProviderServiceAccountOptions(employee);
        },
        getSelectedProviderServiceAccount(effort) {
            const employee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            const selected = this.getEmployeeProviderServiceAccountById(
                employee,
                effort && effort.provider_service_account_id
            );
            if (selected) {
                return selected;
            }

            const snapshotId = Number(effort && effort.provider_service_account_id || 0);
            const snapshotName = String(effort && effort.provider_service_account_name_snapshot || '').trim();
            if (snapshotId > 0 && snapshotName !== '') {
                return {
                    id: snapshotId,
                    name: snapshotName,
                    holder_name: effort.provider_company_name_snapshot || '',
                    holder_image: employee ? this.getEmployeeAvatarUrl(employee) : '/images/default/icon.png',
                    hourly_rate: effort.provider_hourly_rate,
                    vat_rate: effort.provider_vat_rate,
                };
            }

            return null;
        },
        resolveProviderServiceAccountIdForEditing(effort) {
            const currentId = Number(effort && effort.provider_service_account_id || 0);
            if (currentId > 0) {
                return currentId;
            }
            if (!effort || effort.is_checked_in || ['approved', 'cancelled'].includes(String(effort.status || 'pending'))) {
                return null;
            }

            const employee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            return this.getDefaultProviderServiceAccountId(employee);
        },
        shouldShowProviderServiceAccount(effort) {
            const employee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            return !!employee && !this.isAdminUser(employee);
        },
        canEditProviderServiceAccount(effort) {
            return this.shouldShowProviderServiceAccount(effort)
                && !this.isEffortLockedForCurrentUser(effort)
                && !this.isEmployeeAssignmentReadonly(effort);
        },
        resolveProviderServiceAccountAvatar(account, effort = null) {
            if (account && account.holder_image) {
                return account.holder_image;
            }

            const employee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            return employee ? this.getEmployeeAvatarUrl(employee) : '/images/default/icon.png';
        },
        providerServiceAccountSubtitle(account) {
            if (!account) {
                return '';
            }

            const parts = [account.holder_name].filter((value) => String(value || '').trim() !== '');
            const rate = this.normalizeRateForPayload(account.hourly_rate);
            if (rate !== null) {
                parts.push(`${this.formatOptionalCurrency(rate)} / Std.`);
            }

            return parts.join(' · ');
        },
        setEffortProviderServiceAccount(index, accountId) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index] || !this.canEditProviderServiceAccount(current[index])) {
                return;
            }

            const effort = {...current[index]};
            const normalizedId = Number(accountId || 0);
            const account = this.getEffortProviderServiceAccountOptions(effort)
                .find((item) => Number(item.id) === normalizedId) || null;
            effort.provider_service_account_id = account ? Number(account.id) : null;
            effort.provider_hourly_rate = account ? this.normalizeRateForPayload(account.hourly_rate) : null;
            effort.provider_vat_rate = account ? this.normalizeRateForPayload(account.vat_rate) : null;
            effort.provider_company_id = account && account.company_id ? Number(account.company_id) : null;
            effort.provider_company_name_snapshot = account ? (account.company_name || account.holder_name || null) : null;
            current[index] = effort;
            this.documentation.efforts = current;
        },
        isAdminUser(user) {
            return !!user && !!user.is_admin;
        },
        isAdminEffortSelection(effort) {
            if (!this.isAdmin) {
                return false;
            }

            const selectedEmployee = this.getEffortEmployeeOptionById(this.getPrimaryEffortEmployeeId(effort));
            return this.isAdminUser(selectedEmployee);
        },
        shouldShowInternalBillingForEffort(effort) {
            if (this.isEmployee) {
                return true;
            }

            if (this.isAdminEffortSelection(effort)) {
                return false;
            }

            return true;
        },
        shouldShowEffortQuantificationFields(effort) {
            if (this.isAdmin) {
                return true;
            }

            return this.isEmployee && this.isEffortAssignedToCurrentUser(effort);
        },
        getEffortCategoryName(serviceCategoryId) {
            const category = (this.documentation.effort_catalog || []).find((item) => Number(item.id) === Number(serviceCategoryId));
            return category ? category.name : '-';
        },
        getEffortCategoryDescription(serviceCategoryId) {
            const category = (this.documentation.effort_catalog || []).find((item) => Number(item.id) === Number(serviceCategoryId));
            return category ? (category.short_description || '') : '';
        },
        isEffortApproved(effort) {
            return (effort.status || 'pending') === 'approved';
        },
        isEffortCancelled(effort) {
            return (effort.status || 'pending') === 'cancelled';
        },
        isEffortCheckedIn(effort) {
            return !!effort && !!effort.is_checked_in && !this.isEffortApproved(effort) && !this.isEffortCancelled(effort);
        },
        isEffortAssignedToCurrentUser(effort) {
            if (!this.currentUserId) {
                return false;
            }
            const employeeIds = Array.isArray(effort && effort.employee_ids) ? effort.employee_ids : [];
            return employeeIds.map((id) => Number(id)).includes(Number(this.currentUserId));
        },
        isEffortCustomerSideConfigured(effort) {
            if (!effort) {
                return false;
            }
            if (Object.prototype.hasOwnProperty.call(effort, 'customer_side_configured')) {
                return !!effort.customer_side_configured;
            }

            return effort.customer_hours !== null && effort.customer_hours !== '';
        },
        isEffortLockedForCurrentUser(effort) {
            if (this.isEffortCancelled(effort) || this.isEffortApproved(effort) || this.isEffortCheckedIn(effort)) {
                return true;
            }
            if (this.isEmployee && !this.isEffortAssignedToCurrentUser(effort)) {
                return true;
            }
            return !this.documentation.can_edit;
        },
        isInternalHoursReadonly(effort) {
            if (this.isEffortLockedForCurrentUser(effort)) {
                return true;
            }
            if (this.isAdminEffortSelection(effort)) {
                return true;
            }
            if (!this.isAdmin && this.isEffortCustomerSideConfigured(effort)) {
                return true;
            }
            return false;
        },
        isCustomerHoursReadonly(effort) {
            if (this.isEffortLockedForCurrentUser(effort)) {
                return true;
            }
            return !this.isAdmin;
        },
        isEffortQuantificationReadonly(effort) {
            if (this.isEffortLockedForCurrentUser(effort)) {
                return true;
            }
            if (this.isAdmin) {
                return false;
            }

            return this.isEffortCustomerSideConfigured(effort);
        },
        isEmployeeAssignmentReadonly(effort) {
            if (this.isEffortLockedForCurrentUser(effort)) {
                return true;
            }
            if (!this.isAdmin && this.isEffortCustomerSideConfigured(effort)) {
                return true;
            }
            return false;
        },
        setEffortField(index, key, value) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index]) {
                return;
            }
            const effort = {...current[index]};
            effort[key] = value;
            current[index] = effort;
            this.documentation.efforts = current;
        },
        setEffortEmployee(index, userId) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index]) {
                return;
            }

            const effort = {...current[index]};
            const normalizedUserId = Number(userId || 0);
            effort.employee_ids = normalizedUserId > 0 ? [normalizedUserId] : [];
            const selectedEmployee = this.getEffortEmployeeOptionById(normalizedUserId);
            if (this.isAdmin && this.isAdminUser(selectedEmployee)) {
                effort.internal_hours = null;
            }
            const defaultProviderServiceAccountId = this.isAdminUser(selectedEmployee)
                ? null
                : this.getDefaultProviderServiceAccountId(selectedEmployee);
            const defaultProviderServiceAccount = this.getEmployeeProviderServiceAccountById(
                selectedEmployee,
                defaultProviderServiceAccountId
            );
            effort.provider_service_account_id = defaultProviderServiceAccountId;
            effort.provider_hourly_rate = defaultProviderServiceAccount
                ? this.normalizeRateForPayload(defaultProviderServiceAccount.hourly_rate)
                : null;
            effort.provider_vat_rate = defaultProviderServiceAccount
                ? this.normalizeRateForPayload(defaultProviderServiceAccount.vat_rate)
                : null;
            effort.customer_hourly_rate = this.getDefaultCustomerHourlyRateForEffort(effort);
            current[index] = effort;
            this.documentation.efforts = current;
        },
        toggleEffortEmployee(index, userId) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index]) {
                return;
            }

            const effort = {...current[index]};
            const employeeIds = Array.isArray(effort.employee_ids) ? [...effort.employee_ids] : [];
            const exists = employeeIds.includes(Number(userId));
            effort.employee_ids = exists
                ? employeeIds.filter((id) => Number(id) !== Number(userId))
                : [...employeeIds, Number(userId)];
            current[index] = effort;
            this.documentation.efforts = current;
        },
        addEffort() {
            const serviceCategoryId = Number(this.newEffortServiceCategoryId || 0);
            if (!this.canAddEffort || serviceCategoryId < 1) {
                return;
            }

            const current = [...(this.documentation.efforts || [])];
            const employee_ids = this.resolveEffortEmployeeIdsForEditing({employee_ids: []});
            const draftEffort = {
                employee_ids,
            };
            const defaultEmployee = this.getEffortEmployeeOptionById(employee_ids[0]);
            const providerServiceAccountId = this.isAdminUser(defaultEmployee)
                ? null
                : this.getDefaultProviderServiceAccountId(defaultEmployee);
            const providerServiceAccount = this.getEmployeeProviderServiceAccountById(
                defaultEmployee,
                providerServiceAccountId
            );
            current.push({
                id: null,
                service_category_id: serviceCategoryId,
                internal_hours: null,
                customer_hours: null,
                customer_side_configured: false,
                customer_hourly_rate: this.getDefaultCustomerHourlyRateForEffort(draftEffort),
                quantification_factor: 0,
                quantification_value: 0.25,
                short_description: '',
                employee_ids,
                provider_service_account_id: providerServiceAccountId,
                provider_hourly_rate: providerServiceAccount ? providerServiceAccount.hourly_rate : null,
                provider_vat_rate: providerServiceAccount ? providerServiceAccount.vat_rate : null,
                status: 'pending',
                is_checked_in: false,
                customer_note: null,
                approved_at: null,
                cancelled_at: null,
            });
            this.documentation.efforts = current;
        },
        canRemoveEffort(effort) {
            if (!this.documentation.can_edit || this.isEffortApproved(effort) || this.isEffortCancelled(effort)) {
                return false;
            }
            if (this.isEffortCheckedIn(effort) && !this.isAdmin) {
                return false;
            }
            return this.isAdmin || !this.isEffortCustomerSideConfigured(effort);
        },
        canChangeEffortAssignment(effort) {
            return this.isAdmin
                && !!effort
                && !!effort.can_change_assignment
                && this.getAssignmentChangeOptions(effort).length > 0;
        },
        canCheckInEffort(effort) {
            if (!this.isAdmin || !this.documentation.can_edit) {
                return false;
            }
            if (!effort || this.isEffortApproved(effort) || this.isEffortCancelled(effort) || this.isEffortCheckedIn(effort)) {
                return false;
            }
            return this.getEffortCheckInValidationError(effort) === null;
        },
        isEffortCheckStateSelectionDisabled(effort) {
            if (!this.canManageEffortCheckState(effort)) {
                return true;
            }
            return !this.hasAssignedCompany && !this.isEffortCheckedIn(effort);
        },
        getEffortCheckInValidationError(effort) {
            if (!effort) {
                return this.$i18n.t('Checked-in cost approvals require customer hours and hourly rate').toString();
            }
            if (!this.hasAssignedCompany) {
                return this.$i18n.t('Checked-in cost approvals require an assigned company').toString();
            }

            const employeeIds = Array.isArray(effort.employee_ids)
                ? effort.employee_ids
                    .map((id) => Number(id))
                    .filter((id) => id > 0)
                : [];
            if (employeeIds.length < 1) {
                return this.$i18n.t('Checked-in cost approvals require at least one assigned employee').toString();
            }
            if (!this.isAdminEffortSelection(effort)) {
                const selectedAccount = this.getSelectedProviderServiceAccount(effort);
                const eligibleAccount = this.getEffortProviderServiceAccountOptions(effort)
                    .find((account) => Number(account.id) === Number(selectedAccount && selectedAccount.id || 0));
                if (!eligibleAccount) {
                    return 'Bitte wählen Sie ein aktives Dienstleister-Leistungskonto aus.';
                }
                if (!eligibleAccount.has_valid_rate) {
                    return this.$i18n.t('No valid hourly rate is configured.').toString();
                }
            }

            const customerHours = this.normalizeHoursForPayload(effort.customer_hours);
            const customerHourlyRate = this.normalizeRateForPayload(effort.customer_hourly_rate);
            const quantificationFactor = this.getEffortQuantificationFactor(effort);
            const quantificationValue = this.getEffortQuantificationValue(effort);
            if (customerHours === null || customerHourlyRate === null) {
                return this.$i18n.t('Checked-in cost approvals require customer hours and hourly rate').toString();
            }

            const customerBaseAmount = Math.round(customerHours * customerHourlyRate * 100) / 100;
            const customerAmount = Math.round(customerBaseAmount * (1 + (quantificationFactor * quantificationValue)) * 100) / 100;
            const internalHours = this.isAdminEffortSelection(effort)
                ? null
                : this.normalizeHoursForPayload(effort.internal_hours);
            let internalAmount = 0;
            if (internalHours !== null && internalHours > 0) {
                const internalHourlyRate = this.normalizeRateForPayload(this.getInternalHourlyRateForEffort(effort));
                if (internalHourlyRate === null) {
                    return this.$i18n.t('Checked-in cost approvals require internal hours and hourly rate').toString();
                }
                const internalBaseAmount = Math.round(internalHours * internalHourlyRate * 100) / 100;
                internalAmount = Math.round(internalBaseAmount * (1 + (quantificationFactor * quantificationValue)) * 100) / 100;
            }

            if (internalAmount > (customerAmount + 0.00001)) {
                return this.$i18n.t('Checked-in cost approvals require internal amount not greater than customer amount').toString();
            }

            return null;
        },
        checkStateSelectionStyle(isDisabled) {
            if (!isDisabled) {
                return {};
            }

            return {
                color: UI_COLORS.text.subtle,
                WebkitTextFillColor: UI_COLORS.text.subtle,
            };
        },
        canManageEffortCheckState(effort) {
            if (!this.isAdmin || !this.documentation.can_edit) {
                return false;
            }
            return !this.isEffortApproved(effort) && !this.isEffortCancelled(effort);
        },
        setEffortCheckState(index, checkedIn) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index]) {
                return;
            }

            const effort = {...current[index]};
            if (!this.canManageEffortCheckState(effort)) {
                return;
            }

            if (checkedIn) {
                const validationMessage = this.getEffortCheckInValidationError(effort);
                if (validationMessage !== null) {
                    this.$notify({
                        title: this.$i18n.t('Error').toString(),
                        text: validationMessage,
                        type: 'error'
                    });
                    return;
                }
            }

            effort.is_checked_in = !!checkedIn;
            current[index] = effort;
            this.documentation.efforts = current;
        },
        canShowEffortActionsMenu(effort) {
            return this.canChangeEffortAssignment(effort)
                || (this.isAdmin && this.isEffortApproved(effort))
                || this.canRemoveEffort(effort);
        },
        removeEffort(index) {
            const current = [...(this.documentation.efforts || [])];
            if (!current[index] || !this.canRemoveEffort(current[index])) {
                return;
            }
            current.splice(index, 1);
            this.documentation.efforts = current;
        },
        cancelEffort(index) {
            if (!this.isAdmin) {
                return;
            }
            const current = [...(this.documentation.efforts || [])];
            if (!current[index] || !this.isEffortApproved(current[index])) {
                return;
            }
            current[index] = {
                ...current[index],
                cancel: true,
                status: 'cancelled',
            };
            this.documentation.efforts = current;
        },
        effortStatusLabel(effort) {
            if (this.isEffortCancelled(effort)) {
                return 'Storniert';
            }
            if (this.isEffortApproved(effort)) {
                return 'Freigegeben';
            }
            if (this.isEffortCheckedIn(effort)) {
                return 'Freigabe ausstehend';
            }
            return 'Entwurf';
        },
        effortStatusStripClass(effort) {
            if (this.isEffortCancelled(effort)) {
                return 'bg-red-500';
            }
            if (this.isEffortApproved(effort)) {
                return 'bg-green-500';
            }
            return 'bg-bordergray-100';
        },
        isActionApprovalApproved(item) {
            return (item.status || 'pending') === 'approved';
        },
        isActionApprovalCancelled(item) {
            return (item.status || 'pending') === 'cancelled';
        },
        isActionApprovalCheckedIn(item) {
            return !!item && !!item.is_checked_in && !this.isActionApprovalApproved(item) && !this.isActionApprovalCancelled(item);
        },
        isActionApprovalLockedForCurrentUser(item) {
            if (this.isActionApprovalCancelled(item) || this.isActionApprovalApproved(item) || this.isActionApprovalCheckedIn(item)) {
                return true;
            }
            return !this.documentation.can_edit;
        },
        setActionApprovalField(index, key, value) {
            const current = [...(this.documentation.action_approvals || [])];
            if (!current[index]) {
                return;
            }
            const item = {...current[index]};
            item[key] = value;
            current[index] = item;
            this.documentation.action_approvals = current;
        },
        addActionApproval() {
            if (!this.canAddActionApproval) {
                return;
            }
            const current = [...(this.documentation.action_approvals || [])];
            current.push({
                id: null,
                title: this.newActionApprovalTitle.trim(),
                short_description: '',
                status: 'pending',
                is_checked_in: false,
                customer_note: null,
                approved_at: null,
                cancelled_at: null,
            });
            this.documentation.action_approvals = current;
            this.newActionApprovalTitle = '';
        },
        canRemoveActionApproval(item) {
            if (!this.documentation.can_edit || this.isActionApprovalApproved(item) || this.isActionApprovalCancelled(item)) {
                return false;
            }
            if (this.isActionApprovalCheckedIn(item) && !this.isAdmin) {
                return false;
            }
            return true;
        },
        canCheckInActionApproval(item) {
            if (!this.isAdmin || !this.documentation.can_edit) {
                return false;
            }
            if (!item || this.isActionApprovalApproved(item) || this.isActionApprovalCancelled(item) || this.isActionApprovalCheckedIn(item)) {
                return false;
            }
            return this.getActionApprovalCheckInValidationError(item) === null;
        },
        isActionApprovalCheckStateSelectionDisabled(item) {
            if (!this.canManageActionApprovalCheckState(item)) {
                return true;
            }
            return !this.hasAssignedCompany && !this.isActionApprovalCheckedIn(item);
        },
        getActionApprovalCheckInValidationError(item) {
            if (!this.hasAssignedCompany) {
                return this.$i18n.t('Checked-in action approvals require an assigned company').toString();
            }
            if (!item || String(item.title || '').trim() === '') {
                return this.$i18n.t('The :attribute field is required', {attribute: this.$i18n.t('title').toString()}).toString();
            }
            return null;
        },
        canManageActionApprovalCheckState(item) {
            if (!this.isAdmin || !this.documentation.can_edit) {
                return false;
            }
            return !this.isActionApprovalApproved(item) && !this.isActionApprovalCancelled(item);
        },
        setActionApprovalCheckState(index, checkedIn) {
            const current = [...(this.documentation.action_approvals || [])];
            if (!current[index]) {
                return;
            }

            const item = {...current[index]};
            if (!this.canManageActionApprovalCheckState(item)) {
                return;
            }

            if (checkedIn) {
                const validationMessage = this.getActionApprovalCheckInValidationError(item);
                if (validationMessage !== null) {
                    this.$notify({
                        title: this.$i18n.t('Error').toString(),
                        text: validationMessage,
                        type: 'error'
                    });
                    return;
                }
            }

            item.is_checked_in = !!checkedIn;
            current[index] = item;
            this.documentation.action_approvals = current;
        },
        canShowActionApprovalActionsMenu(item) {
            return (this.isAdmin && this.isActionApprovalApproved(item))
                || this.canRemoveActionApproval(item);
        },
        removeActionApproval(index) {
            const current = [...(this.documentation.action_approvals || [])];
            if (!current[index] || !this.canRemoveActionApproval(current[index])) {
                return;
            }
            current.splice(index, 1);
            this.documentation.action_approvals = current;
        },
        cancelActionApproval(index) {
            if (!this.isAdmin) {
                return;
            }
            const current = [...(this.documentation.action_approvals || [])];
            if (!current[index] || !this.isActionApprovalApproved(current[index])) {
                return;
            }
            current[index] = {
                ...current[index],
                cancel: true,
                status: 'cancelled',
            };
            this.documentation.action_approvals = current;
        },
        actionApprovalStatusLabel(item) {
            if (this.isActionApprovalCancelled(item)) {
                return 'Storniert';
            }
            if (this.isActionApprovalApproved(item)) {
                return 'Freigegeben';
            }
            if (this.isActionApprovalCheckedIn(item)) {
                return 'Freigabe ausstehend';
            }
            return 'Entwurf';
        },
        actionApprovalStatusStripClass(item) {
            if (this.isActionApprovalCancelled(item)) {
                return 'bg-red-500';
            }
            if (this.isActionApprovalApproved(item)) {
                return 'bg-green-500';
            }
            return 'bg-bordergray-100';
        },
        toggleStepEditor(stepId) {
            const processStep = (this.documentation.process_steps || []).find((step) => Number(step.id) === Number(stepId));
            if (!processStep || !this.documentation.can_edit) {
                return;
            }
            this.activeStepEditorId = this.activeStepEditorId === stepId ? null : stepId;
        },
        getProcessStepReaders(processStep) {
            const stepReaderIds = Array.isArray(processStep && processStep.reader_ids) ? processStep.reader_ids : [];
            if (stepReaderIds.length < 1) {
                return [];
            }

            const readersById = {};
            (this.documentation.step_reader_options || []).forEach((reader) => {
                readersById[Number(reader.id)] = reader;
            });

            return stepReaderIds
                .map((readerId) => readersById[Number(readerId)])
                .filter((reader) => !!reader)
                .slice(0, 5);
        },
        getUserAvatarUrl(user) {
            return getPositionUserAvatarUrl(user);
        },
        getApprovalAvatarUrl(user) {
            return getPositionApprovalAvatarUrl(user, this.documentation?.company?.image || '');
        },
        getEmployeeAvatarUrl(employee) {
            return getPositionEmployeeAvatarUrl(employee, [
                ...(Array.isArray(this.documentation.employee_options) ? this.documentation.employee_options : []),
                ...(Array.isArray(this.documentation.readers) ? this.documentation.readers : []),
                ...(Array.isArray(this.documentation.step_reader_options) ? this.documentation.step_reader_options : []),
            ]);
        },
        getUniqueUsersById(users) {
            return getUniquePositionUsersById(users);
        },
        getEmployeeAssignmentSubtitle(employee) {
            return getEmployeeAssignmentSubtitle(employee);
        },
        getPositionAssignedEmployees() {
            return getAssignedEmployeesByIds(
                this.documentation && this.documentation.assigned_employee_ids,
                this.documentation.employee_options || []
            );
        },
        getEffortAssignedEmployees(effort) {
            return getAssignedEmployeesByIds(effort && effort.employee_ids, this.documentation.employee_options || []);
        },
        handleAvatarImageError(event, user) {
            applyAvatarImageFallback(event, user);
        },
        handleApprovalAvatarImageError(event, user) {
            applyApprovalAvatarImageFallback(event, user, this.documentation?.company?.image || '');
        },
        formatStepDate(value) {
            return formatPositionStepDate(value);
        },
        formatProcessStepCompletedDate(value) {
            if (!value) {
                return '--.--.----';
            }
            return this.formatStepDate(value);
        },
        isStepOverdue(processStep) {
            return isPositionStepOverdue(processStep);
        },
        getApprovalStatusLabel(status) {
            return getApprovalStatusLabel(status);
        },
        getApprovalStatusText(item) {
            return getApprovalStatusText(item);
        },
        getApprovalCheckinOrDraftText(item) {
            return getApprovalCheckinOrDraftText(item);
        },
        getApprovalApprovedAtText(item) {
            return getApprovalApprovedAtText(item);
        },
        getApprovalApprovedAtDateTimeText(item) {
            return getApprovalApprovedAtDateTimeText(item);
        },
        getApprovalEffortHoursText(item) {
            return getApprovalEffortHoursText(item, this.canCurrentUserSeeEffortAmounts(item));
        },
        canCurrentUserSeeEffortAmounts(effort) {
            if (!this.isEmployee) {
                return true;
            }
            return this.isEffortAssignedToCurrentUser(effort);
        },
        getApprovalStatusBorderColor(status) {
            return getApprovalStatusBorderColor(status);
        },
        getProcessStepTimelineDotStyle(color) {
            return getProcessStepTimelineDotStyle(color);
        },
        withOpacity(color, opacity) {
            return withOpacity(color, opacity);
        },
        hasStepBody(processStep) {
            return hasStepBody(processStep);
        },
        formatStepBodyUpdatedAt(value) {
            return formatStepBodyUpdatedAt(value);
        },
        formatBodyUpdateMeta(updatedAt, user) {
            return formatBodyUpdateMeta(updatedAt, user);
        },
        formatCurrency(value) {
            return formatFinanceCurrency(value);
        },
        formatOptionalCurrency(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return '-';
            }

            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) {
                return '-';
            }

            return this.formatCurrency(numericValue);
        },
        formatSignedCurrency(value) {
            return formatFinanceSignedCurrency(value);
        },
        hasShortDescriptionUpdateMeta(item) {
            return !!item
                && !!item.short_description_updated_at
                && !!item.short_description_updated_by
                && String((item.short_description_updated_by.name || '')).trim() !== '';
        },
        deletePosition() {
            const self = this;
            if (!self.documentation.can_manage_permissions) {
                self.deletePositionModal = false;
                return;
            }
            if (!self.documentation.can_delete) {
                self.deletePositionModal = false;
                if (self.documentation.delete_block_reason) {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: self.documentation.delete_block_reason,
                        type: 'error'
                    });
                }
                return;
            }

            self.loading.deleting = true;
            axios.delete('api/workspace/positions/' + self.$route.params.id).then(function () {
                self.loading.deleting = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push(self.positionsListPath);
            }).catch(function (error) {
                self.loading.deleting = false;
                self.deletePositionModal = false;
                const message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '';
                if (message !== '') {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: message,
                        type: 'error'
                    });
                }
            });
        },
    }
}
</script>

<style lang="scss">
.documentation-sidebar {
    min-height: calc(100vh - 70px);
}

.documentation-permission-icons {
    color: var(--ui-text-subtle);
}

.documentation-permission-icon svg,
.documentation-permission-icon svg * {
    fill: var(--ui-text-subtle) !important;
    stroke: var(--ui-text-subtle) !important;
}

.documentation-editor .tox.tox-tinymce {
    min-height: 320px;
}
</style>
